// Feature: MCRO001-F0006 (video library presentation); related F0026 (demonstration
// attach) and F0021/F0262 (media access + provenance). Owner redesign 2026-07-24:
// the Video library tab is a calm, caption-free media gallery — a thumbnail grid of
// videos AND photos, multi-select for group operations (drag toward the canvas /
// "Timing editor"), quick-play on hover without expanding, click-to-expand with an
// inline player and editable metadata (PATCH /media/:mediaId), and a right-side
// access drawer showing real access data plus real provenance (who has access and
// where each right came from) from the M001-F0262 access-explain endpoint.
// Traceability note: the drawer's own Feature/Test ID is pending reconciliation in
// the M004-D015 spec review (MR !173) — do not confuse this with M004-F0395 (a
// distinct PanOptic camera-tab feature); this surface is MCRO001-D003 territory.
//
// The dense demonstration-timing cockpit (DemonstrationWorkspace) is NOT deleted; it
// is reached deliberately through the "Timing editor" flow instead of being the
// landing surface of the tab.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { trackColourForMedia } from './mediaTrackColour';
import { useRefreshingStream } from '../../utils/streamRefresh';
// WebUI issue #30 / M001-D023 §7 §12.1: the same conformant share panel the
// Sheet uses, bound to resourceType choreo_media below.
import ResourceSharePanel from '../sharing/ResourceSharePanel';
import useDismissOnOutsideClick from '../../hooks/useDismissOnOutsideClick';

// The real mint client is required lazily (not statically imported) so that
// merely importing MediaLibraryGrid.js — e.g. for its DRAG_MIME constant, as
// several sibling components/tests do — never pulls in axios/services/api.js
// as a side effect. It is only actually loaded the first time a stream URL is
// minted for real (i.e. when no `mintStreamUrl` override prop was supplied).
export function defaultMintStreamUrl(orgId, mediaId, variant) {
  // eslint-disable-next-line global-require
  const { choreographyApi } = require('../../services/api');
  return choreographyApi.mintMediaStreamUrl(orgId, mediaId, variant);
}

const mediaKey = (item) => String(item?.mediaId || item?.id || '');

// MCRO001-F0053 / T-MCRO-053 (MCRO001-D011 §4.3): the server's own nesting depth
// cap, mirrored here ONLY as a UX nicety — proactively disabling "New folder"
// once a location is already at the cap, so the actor sees why before trying
// rather than only after a 422. The server (choreographyMediaFolders.service.ts,
// choreo repo, already merged) remains the sole authority; this constant must
// never let the client accept something the server would refuse, only refuse a
// touch earlier for a location the server would refuse anyway. Move operations
// deliberately do NOT precompute this client-side (the resulting depth depends
// on the depth of the subtree being moved, which the client does not know) —
// the picker surfaces the server's own CHOREOGRAPHY_FOLDER_DEPTH_EXCEEDED /
// CHOREOGRAPHY_FOLDER_CYCLE message instead (see FolderDestinationPicker).
const FOLDER_DEPTH_CAP = 5;
const folderKey = (folder) => String(folder?.folderId || '');
const isImageKind = (kind) => /image|photo|picture|still|frame/i.test(String(kind || ''));
export const mediaIsImage = (item) => isImageKind(item?.kind) || /^image\//i.test(String(item?.mimeType || item?.contentType || ''));

// A stored title should never render raw if it's actually an empty/near-empty
// JSON-ish value — e.g. a stray "[]", "{}", "null", or "undefined" that reached
// the title column instead of a real label (data bug, not a display choice).
// This is a pure display fallback: it never rewrites stored data, only what's
// shown, and falls back to "Untitled media"/"Untitled video" rather than
// rendering something that looks like broken JSON to the user.
const JSON_ISH_EMPTY_TITLE = /^(\[\s*\]|\{\s*\}|null|undefined)$/i;
export function sensibleTitle(item, fallback = 'Untitled media') {
  const raw = typeof item?.title === 'string' ? item.title.trim() : '';
  if (raw && !JSON_ISH_EMPTY_TITLE.test(raw)) return raw;
  return fallback;
}
const kindLabel = (item) => {
  if (mediaIsImage(item)) return 'Photo';
  if (String(item?.kind) === 'walkthrough_video') return 'Walkthrough';
  return 'Video';
};
export const DRAG_MIME = 'application/x-studioaeye-media';

// T-MCRO-055 / choreo #51 (WebUI job 204084, pipeline 8683, 2026-09-07): how
// long a REAL drag session may stop delivering drag events before the grid
// treats the session as dead and hands the frozen view back. The HTML drag-and
// drop spec REQUIRES a live session to keep firing `drag` on the source and
// `dragover` at the current target at ≤350ms intervals even while the pointer
// is held perfectly still, so 1200ms of total drag-event silence is more than
// three missed beats — unreachable for a living drag, and a clean positive
// signature for the eaten-drop death (drop/dragleave/dragend all swallowed by
// the browser's input race, job 204084 attempt 1) that otherwise froze the
// listing forever. See the liveness effect beside the other T-MCRO-158 guards.
const DRAG_LIVENESS_SILENCE_BOUND_MS = 1200;
const DRAG_LIVENESS_TICK_MS = 250;

// Prefer the backend's own message (errorHandler.ts always sends
// { success:false, error: CODE, message: '...human readable...' }) over Axios's
// generic "Request failed with status code NNN".
function libraryErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
}

// MCRO001-F0035 / T-MCRO-035 ("Required behaviour": an authorised user selects or
// drags one or more JPEG/PNG/WebP images...): the upload control (file picker
// `accept` attribute and drag-drop handler) admits image/jpeg, image/png and
// image/webp alongside the existing video types, routed through the exact same
// UploadQueueManager chunked-upload flow — no separate image-only upload path.
// HEIC/HEIF is deliberately excluded (case B: rejected format). This client-side
// allowlist is a first line of defense/UX only: the server (video-ingest) is the
// authoritative validator (case C/D/E: size/dimension/malformed-input checks),
// never client-supplied filename or MIME type.
//
// (This capability was originally drafted under MR !54 as MCRO001-F0034/
// T-MCRO-034; that spec MR was closed and F0034 was reallocated to an unrelated
// Rehearsal-transport feature. F0035/T-MCRO-035 is the current, correct spec.)
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const ACCEPTED_UPLOAD_TYPES = [...ACCEPTED_VIDEO_TYPES, ...ACCEPTED_IMAGE_TYPES];
const ACCEPTED_UPLOAD_TYPES_ATTR = ACCEPTED_UPLOAD_TYPES.join(',');
const isAcceptedUploadFile = (file) => ACCEPTED_UPLOAD_TYPES.includes(String(file?.type || '').toLowerCase());

// MCRO001-F0032 / T-MCRO-032 cases G/H/I: media playback and posters stream
// directly against short-lived, variant-locked signed URLs minted by the choreo
// stream-url endpoint (studioaeye-choreo MR !52), rather than fetching an
// authorised blob and wrapping it in URL.createObjectURL. The mint call is the
// only Bearer-gated request in this path — its response (a relative
// /v1/orgs/.../stream?token=... URL) is handed straight to a <video>/<img>
// src/poster attribute, which the browser requests same-origin with no
// Authorization header, verified instead by the short-lived signed token.
//
// Module-level cache of minted URLs within their validity window: tiles
// re-render often (selection, filtering, other state changes) and re-minting a
// token on every render would be wasteful even though the mint endpoint is
// cheap. Keyed by orgId+mediaId+variant; entries are dropped a small safety
// margin before their real expiresAt so a stream request is never attempted
// against an already-expired token.
const streamUrlCache = new Map();
const STREAM_URL_SAFETY_MARGIN_MS = 15000;
// Test-only escape hatch: the cache is intentionally module-level (shared across
// every render of this component within one page load), which also means it is
// shared across tests within one Jest module instance unless explicitly cleared.
export function __clearStreamUrlCacheForTests() { streamUrlCache.clear(); }

// MCRO001-F0039 / T-MCRO-039 Case A regression guard — the reason bulk poster
// minting is scheduled rather than fired all at once.
//
// Every tile mints its own poster URL on mount (T-MCRO-032 case H), and this grid
// renders the WHOLE library: on TestDeploy that is 144 tiles, in a real studio it
// only grows. Fired unbounded, those are 144 simultaneous XHRs against one origin,
// and a browser will only open a small fixed number of connections per origin over
// HTTP/1.1 (6 in every current engine). Everything else queues — including any
// request the user causes AFTERWARDS.
//
// That is what broke the canvas ✕. `detachFromCanvas` issues DELETE
// .../routines/:id/media/:id/link through the shared axios client, whose timeout is
// 10s (src/services/api.js). Queued behind ~144 poster mints at ~300ms each it was
// never dispatched inside that budget, so axios aborted it: the canvas pane showed
// "timeout of 10000ms exceeded", the optimistic removal rolled back, and the tile
// reappeared. The removal simply did not happen.
//
// Measured, not inferred:
//   * job 84806's failure snapshot has the canvas pane rendering the app's own
//     axios message, "timeout of 10000ms exceeded", beside 144 library tiles — so
//     the client aborted its own request;
//   * the deployed service answers DELETE .../link in ~0.4s when called directly,
//     so the 10s was never spent server-side;
//   * loading this tab against the deployed build fires one mint per tile with no
//     bound whatsoever — 161 tiles, 161 requests, peak 161 outstanding.
//
// So bulk poster minting is capped, leaving connections free for whatever the user
// does next. The observable contract of case H is unchanged — every tile still
// mints its poster automatically on mount, with no hover/quick-play gate, and still
// renders it "as soon as it is available". Only the arrival order is scheduled.
//
// USER-INITIATED mints (quick-play `preview`, the expanded player's `content`) are
// deliberately NOT queued: they are one-at-a-time and must never wait behind the
// backlog of a large library.
const BULK_MINT_MAX_CONCURRENCY = 4;
let bulkMintInFlight = 0;
const bulkMintQueue = [];

function pumpBulkMintQueue() {
  while (bulkMintInFlight < BULK_MINT_MAX_CONCURRENCY && bulkMintQueue.length > 0) {
    const run = bulkMintQueue.shift();
    bulkMintInFlight += 1;
    run().finally(() => { bulkMintInFlight -= 1; pumpBulkMintQueue(); });
  }
}

/** Runs `task` under the bulk-mint concurrency cap. Resolves/rejects with `task`. */
function scheduleBulkMint(task) {
  return new Promise((resolve, reject) => {
    bulkMintQueue.push(() => Promise.resolve().then(task).then(resolve, reject));
    pumpBulkMintQueue();
  });
}

// Test-only: the scheduler is module-level for the same reason streamUrlCache is.
export function __resetBulkMintQueueForTests() { bulkMintQueue.length = 0; bulkMintInFlight = 0; }
export function __bulkMintConcurrencyLimitForTests() { return BULK_MINT_MAX_CONCURRENCY; }

function cacheKey(orgId, mediaId, variant) { return `${orgId}:${mediaId}:${variant}`; }

function cachedStreamUrl(orgId, mediaId, variant) {
  const entry = streamUrlCache.get(cacheKey(orgId, mediaId, variant));
  if (!entry) return null;
  if (Date.now() >= entry.usableUntil) { streamUrlCache.delete(cacheKey(orgId, mediaId, variant)); return null; }
  return { url: entry.url, expiresAt: entry.expiresAt };
}

// M004-F0779 / WebUI #251: drop the cached URL so the next mint is a fresh call.
// The mid-play renewal of a <video> uses this before re-minting.
export function invalidateStreamUrl(orgId, mediaId, variant) {
  streamUrlCache.delete(cacheKey(orgId, mediaId, variant));
}

// MCRO001-F0059 / T-MCRO-059 (perf, owner report "Choreo is slow to load",
// 2026-08-26): the choreo backend now returns a pre-minted `posterUrl` /
// `posterUrlExpiresAt` on every row of GET /media and GET
// /routines/:routineId/media (the SAME signMediaStreamToken mechanism as the
// standalone mint endpoint, computed with the SAME image/video variant rule
// as `posterVariant` below -- see the backend route comment). When that URL
// is present and not yet within its own safety margin of expiring, use it
// directly and skip the POST .../stream-url round trip entirely -- this is
// what actually removes the N+1 (T-MCRO-039's bulk-mint cap only ever paced
// it). Only ever consulted for the automatic bulk poster/content mint
// (`bulk === true`, MediaTile's one call site below); user-initiated mints
// (quick-play `preview`, the expanded player's `content`) always mint fresh,
// unchanged.
function inlineStreamUrl(item) {
  const url = item?.posterUrl;
  if (!url) return null;
  const expiresAt = Date.parse(item?.posterUrlExpiresAt || '');
  if (Number.isFinite(expiresAt) && expiresAt - STREAM_URL_SAFETY_MARGIN_MS <= Date.now()) return null;
  return { url, expiresAt };
}

// Mint (or reuse a cached, still-valid) signed stream URL for one media item +
// variant. Resolves to `{ url, expiresAt }` (expiresAt in epoch ms, NaN if unknown).
// `mintStreamUrl` defaults to the real choreo API call but is
// overridable by callers/tests (see MediaLibraryGrid's `mintStreamUrl` prop).
// `bulk` marks the automatic one-per-tile poster mint, which is scheduled under
// BULK_MINT_MAX_CONCURRENCY; user-initiated mints are issued immediately.
async function resolveStreamUrl(mintStreamUrl, orgId, mediaId, variant, bulk = false, item = null) {
  const cached = cachedStreamUrl(orgId, mediaId, variant);
  if (cached) return cached;
  if (bulk) {
    const inline = inlineStreamUrl(item);
    if (inline) {
      const usableUntil = Number.isFinite(inline.expiresAt) ? inline.expiresAt - STREAM_URL_SAFETY_MARGIN_MS : Date.now() + STREAM_URL_SAFETY_MARGIN_MS;
      streamUrlCache.set(cacheKey(orgId, mediaId, variant), { url: inline.url, expiresAt: inline.expiresAt, usableUntil });
      return { url: inline.url, expiresAt: inline.expiresAt };
    }
  }
  const mint = () => Promise.resolve(mintStreamUrl(orgId, mediaId, variant));
  const response = await (bulk ? scheduleBulkMint(mint) : mint());
  // The choreo service wraps every JSON body in its ok() envelope
  // `{ success, data }`, so an axios response carries the payload at
  // `response.data.data`. Reading `response.data.url` here (pre-fix) meant the
  // url was ALWAYS undefined in production — every tile poster and minted
  // playback failed to grey placeholders (2026-08-06, media de8cf980…/
  // beb0c19d…) while the mint endpoint itself returned 200. Same tolerant
  // unwrap pattern as ChoreoSharePanel/CommunityReview et al.
  const payload = response?.data?.data || response?.data;
  const url = payload?.url;
  if (!url) throw new Error('No stream URL was returned.');
  const expiresAt = Date.parse(payload?.expiresAt || '');
  const usableUntil = Number.isFinite(expiresAt) ? expiresAt - STREAM_URL_SAFETY_MARGIN_MS : Date.now() + STREAM_URL_SAFETY_MARGIN_MS;
  streamUrlCache.set(cacheKey(orgId, mediaId, variant), { url, expiresAt, usableUntil });
  return { url, expiresAt };
}

// Mints a signed stream URL for one media item + variant and exposes it as
// element-ready `src`/`poster` state. `active` gates minting the same way the
// old blob hook gated fetching — nothing is minted until the caller actually
// needs the URL (e.g. quick-play triggered, or the expanded player is open).
// Exported so CanvasMediaPanel's tiles can mint their poster through the exact
// same cache + bulk-mint queue + mint call as a library MediaTile (issue #22:
// "same poster source the library uses... do not invent a second path") rather
// than a parallel implementation that could drift from this one.
export function useStreamUrl(mintStreamUrl, orgId, item, variant, active, bulk = false) {
  const [state, setState] = useState({ url: '', status: 'idle', error: '' });
  const key = mediaKey(item);
  useEffect(() => {
    let cancelled = false;
    if (!active || !key || !orgId || typeof mintStreamUrl !== 'function') { setState({ url: '', status: 'idle', error: '' }); return undefined; }
    setState((prev) => (prev.status === 'ready' ? prev : { url: '', status: 'loading', error: '' }));
    resolveStreamUrl(mintStreamUrl, orgId, key, variant, bulk, item).then(({ url }) => {
      if (cancelled) return;
      setState({ url, status: 'ready', error: '' });
    }).catch((error) => {
      if (cancelled) return;
      setState({ url: '', status: 'error', error: error?.message || 'This media could not be played.' });
    });
    return () => { cancelled = true; };
    // `item` is read here for its `posterUrl`/`posterUrlExpiresAt` but deliberately
    // NOT added to this effect's own dependency array: it is tracked via its stable
    // `key` (mediaKey) above, and only needs to be read once, at mint time, not on
    // every unrelated re-render of the same tile object (this project has no
    // exhaustive-deps lint rule configured, so this is a plain code comment, not a
    // rule suppression).
  }, [mintStreamUrl, orgId, key, variant, active, bulk]);
  return state;
}

// A single gallery tile: caption-free by design. Poster frame (image) or a calm video
// placeholder; a hover/focus ▶ overlay quick-plays in place; the tile itself expands
// on click; a hover checkbox drives multi-select; the tile is a drag source carrying
// the current selection's media ids.
//
// Owner direction 2026-07-24: two small icon affordances, "an 'i' in a circle"
// for more info and a "[]" box to invite expanding to the hi-res player. Both
// live in their own `.mc-tile-icons` wrapper (kept deliberately isolated from
// the tile's other markup) so they never collide with wherever a concurrent
// preview-rendition/poster-still change adds its own tile markup. Neither icon
// may trigger tile selection, the drag gesture, or quick-play — both stop
// propagation and prevent default so the parent tile's own handlers never fire.
//
// T-MCRO-032 case H: every tile shows a poster/thumbnail as soon as it's available,
// independent of quick-play — minted on mount, not gated on hover/quick-play. Video
// tiles mint a `poster`-variant URL (a small JPEG); image tiles mint their `content`
// variant directly as the tile image (the backend's isVideoIngestBacked gate 404s
// `poster` requests for plain image uploads, which are not video-ingest-backed).
// Neither path fetches a blob — both are streamed URLs used directly as <img src>.
//
// MCRO001-F0035 / T-MCRO-035 case F (superseding an earlier, now-closed F0034/
// T-MCRO-034 draft of this same capability, MR !54, which incorrectly proposed a
// `preview` rendition tier for images): an accepted image has exactly ONE stored
// delivery-ready artifact — no poster/preview/full ladder exists or is expected
// for it (MCRO001-D007 §7.2, explicit). The grid tile and the expanded view both
// use that single `content` artifact; tile-sized presentation is ordinary
// client-side `<img>`/CSS scaling, not a second server-side rendition. Do not
// reintroduce an image `preview` mint here without a spec amendment.
//
// MCRO001-F0053 / T-MCRO-053 (MCRO001-D011 §5.3): "Move to folder…" is
// DELIBERATELY NOT a third always-visible icon in `.mc-tile-icons` — that was
// tried first and reverted. `.mc-tile-icons` (top-right, growing leftward) and
// `.mc-tile-select` (top-left) are two independently-positioned absolute
// clusters with no shared collision budget; a real-browser Playwright run
// (T-MCRO-026's `choreography-demonstration-timing-map.spec.ts`, at the video-
// library pane's real narrower-than-full-viewport width) showed the third icon
// pushing `.mc-tile-icons` far enough left to have the info icon's own hit
// area cover the select checkbox, blocking every click on it — jsdom cannot
// catch this (no real layout engine), which is why this was only caught in a
// real-browser CI job, not the component-test suite. "Move to folder…" lives
// in `ExpandedMedia`'s own `.mc-expanded-actions` row instead (see below) —
// already a proven, spacious, no-collision-risk surface for exactly this kind
// of secondary per-item action (Access/Add to canvas/Timing editor already
// live there).
function MediaTile({ item, orgId, mintStreamUrl, selected, onToggleSelect, onExpand, onDragMediaStart, onGesturePointerDown, onGestureDragEnd, quickPlaying, onQuickPlay, onStopQuickPlay, onDelete, deleting, trackColour }) {
  const key = mediaKey(item);
  const image = mediaIsImage(item);
  const label = sensibleTitle(item);
  // MCRO001-F0052: a video that has a Canvas demonstration timing rail shows its
  // tile border in that rail's colour, so the same video is the same colour in the
  // Library and on the Canvas. The colour is the shared media-keyed track colour
  // (mediaTrackColour.js). It rides on a CSS custom property and an is-tracked class
  // so the selection ✓ badge and aria-pressed label stay the non-colour identity
  // cues (colour is never the sole carrier of meaning). A video with no rail line
  // has no track colour and keeps the ordinary selection border.
  const tracked = Boolean(trackColour);
  const posterVariant = image ? 'content' : 'poster';
  // `bulk: true` — one of these fires for EVERY tile in the library at once; it is
  // scheduled under BULK_MINT_MAX_CONCURRENCY so the grid can never starve an
  // interactive request (see the scheduler's comment for the defect this fixes).
  const poster = useStreamUrl(mintStreamUrl, orgId, item, posterVariant, true, true);
  const [posterFailed, setPosterFailed] = useState(false);
  // Case G: a poster 404 (pre-F0032 media not yet backfilled) or any other load
  // failure must fall back to the placeholder glyph, never a broken image icon.
  useEffect(() => { setPosterFailed(false); }, [poster.url]);
  const posterReady = poster.status === 'ready' && !posterFailed;

  // T-MCRO-032 case H: quick-play streams the `preview` variant directly as
  // <video src>, only minted once quick-play is actually triggered.
  // M004-F0779 / WebUI #251: the preview <video> renews its URL mid-play on error/stall near expiry.
  const preview = useRefreshingStream(() => { invalidateStreamUrl(orgId, key, 'preview'); return resolveStreamUrl(mintStreamUrl, orgId, key, 'preview'); }, [orgId, key], { active: quickPlaying });

  const onKeyDown = (event) => {
    if (event.key === 'Enter') { event.preventDefault(); onExpand(item, event.currentTarget); }
    else if (event.key === ' ') { event.preventDefault(); onToggleSelect(item, event); }
    else if (event.key.toLowerCase() === 'p') { event.preventDefault(); quickPlaying ? onStopQuickPlay() : onQuickPlay(key); }
  };

  return (
    <div
      className={`mc-tile ${selected ? 'is-selected' : ''} ${quickPlaying ? 'is-playing' : ''} ${tracked ? 'is-tracked' : ''}`}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`${kindLabel(item)}: ${label}${selected ? ', selected' : ''}${tracked ? ', on the canvas timing rail' : ''}. Enter to open, space to select, P to play.`}
      draggable
      // T-MCRO-158: the gesture begins HERE, at the physical press — before
      // any refetch can swap the listing under the pointer — and ends on the
      // drag's dragend (or the document pointerup for a plain click).
      onPointerDown={(event) => onGesturePointerDown(item, event)}
      onDragEnd={onGestureDragEnd}
      onDragStart={(event) => onDragMediaStart(event, item)}
      onClick={(event) => { if (event.target.closest('.mc-tile-select, .mc-tile-play')) return; onExpand(item, event.currentTarget); }}
      onKeyDown={onKeyDown}
      data-media-id={key}
      data-track-colour={tracked ? trackColour : undefined}
      style={tracked ? { '--mc-tile-track-colour': trackColour } : undefined}
    >
      <div className="mc-tile-frame">
        {quickPlaying && preview.src && preview.status !== 'failed'
          ? <video className="mc-tile-video" src={preview.src} {...preview.videoProps} autoPlay muted loop playsInline aria-label={`Preview of ${label}`} />
          : posterReady
            // `loading="lazy"` for the same reason bulk minting is capped: once the
            // mints resolve, one poster GET per tile follows, and a whole-library
            // grid would put the identical queue back in front of the user's next
            // action. Off-screen tiles fetch their poster when scrolled to; on-screen
            // tiles are unaffected, so case H still holds for everything visible.
            ? <img className="mc-tile-image" src={poster.url} alt={label} draggable={false} loading="lazy" decoding="async" onError={() => setPosterFailed(true)} />
            : <div className="mc-tile-placeholder" aria-hidden="true">{image ? '▧' : '▶'}</div>}
        {quickPlaying && preview.status === 'loading' && <div className="mc-tile-loading" role="status">Loading…</div>}
        {quickPlaying && preview.status === 'failed' && (
          <div className="mc-tile-loading" role="alert">
            <span data-testid="stream-refresh-failed">{preview.error}</span>
            <button type="button" data-testid="stream-refresh-retry" onClick={(event) => { event.stopPropagation(); preview.retry(); }}>Retry</button>
          </div>
        )}
      </div>

      <button
        type="button"
        className="mc-tile-select"
        aria-label={selected ? `Deselect ${label}` : `Select ${label}`}
        aria-pressed={selected}
        onClick={(event) => { event.stopPropagation(); onToggleSelect(item, event); }}
      >{selected ? '✓' : ''}</button>

      {!image && (
        <button
          type="button"
          className="mc-tile-play"
          aria-label={quickPlaying ? `Stop preview of ${label}` : `Play ${label} in place`}
          aria-pressed={quickPlaying}
          onClick={(event) => { event.stopPropagation(); quickPlaying ? onStopQuickPlay() : onQuickPlay(key); }}
        >{quickPlaying ? '❙❙' : '▶'}</button>
      )}

      {/* Isolated wrapper for the info/expand affordances — see the function
          comment above for why this stays a separate, clearly-scoped element. */}
      <div className="mc-tile-icons">
        <button
          type="button"
          className="mc-tile-expand"
          aria-label={`Expand ${label} to full size`}
          onClick={(event) => { event.stopPropagation(); event.preventDefault(); onExpand(item, event.currentTarget); }}
          onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') event.stopPropagation(); }}
        ><span aria-hidden="true">⛶</span></button>
        {/* MCRO001-F0039 / T-MCRO-039 Case G: the ONLY destructive control in the
            choreography media UI, and it lives here — on a Library tile — because
            this is the surface that owns the library. The canvas pane's ✕ removes
            canvas membership and nothing else (see CanvasMediaPanel.js).

            Deliberately visually distinct from the canvas ✕: a 🗑 glyph, in the
            Library tile's icon cluster rather than the canvas tile's corner, with
            the `is-destructive` treatment. The two controls must never again be
            the same glyph in the same place meaning different things. */}
        {onDelete && (
          <button
            type="button"
            className="mc-tile-delete is-destructive"
            aria-label={`Delete ${label} from the library`}
            title="Delete from library"
            disabled={deleting}
            onClick={(event) => { event.stopPropagation(); event.preventDefault(); onDelete(item); }}
            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') event.stopPropagation(); }}
          ><span aria-hidden="true">{deleting ? '…' : '🗑'}</span></button>
        )}
      </div>
    </div>
  );
}

// MCRO001-F0030 / T-MCRO-030 Case M (GitLab WebUI #20): "Video library shows
// NOTHING for in-progress or wedged uploads — user sees successful uploads
// 'completely disappear'". A tile for one of the caller's own video-ingest
// sessions that has NOT (yet, or ever) become a real media item — everything
// choreo's GET /video-ingests (choreographyVideoIngestService.listPending)
// returns: admitted/uploading/validating/processing, or a terminal failed/
// cancelled/expired kept visible until the retention sweep removes the row.
// Rendered in the SAME `.mc-grid`/`role="list"` as real MediaTiles (see the
// pendingUploads merge below) so it never reads as a second, disconnected
// surface — it is the same library, honestly reporting an item that is not
// finished yet instead of omitting it.
const PENDING_INGEST_NON_TERMINAL = new Set(['admitted', 'uploading', 'validating', 'processing']);
const PENDING_INGEST_LABEL = {
  admitted: 'Uploading', uploading: 'Uploading', validating: 'Validating…', processing: 'Processing', cancelled: 'Cancelled',
};
export function pendingIngestStatusLabel(row) {
  const lifecycle = row?.lifecycle;
  if (lifecycle === 'failed') return `Failed: ${row?.failureMessage || 'The upload failed.'}`;
  if (lifecycle === 'expired') return 'Expired — start again';
  return PENDING_INGEST_LABEL[lifecycle] || 'Uploading';
}
// GitLab WebUI #29: "no way to dismiss old terminally-failed upload attempts".
// `terminal` below is the ONLY definition of "terminally failed" this feature
// uses, and it is exactly the existing PENDING_INGEST_NON_TERMINAL check that
// already gates the pre-existing "Try again" control — i.e. the SAME server
// lifecycle field (choreo's GET /video-ingests `lifecycle`) decides both. A row
// is never dismissible while its own lifecycle is admitted/uploading/
// validating/processing, so an in-flight or recoverable attempt can never be
// hidden by this control. This deliberately mirrors the choreo #19 lesson (a
// client-tab-driven "this is done" decision stranded two real videos, later
// recovered by server-side reconciliation): the client never invents its own
// notion of "dead" — it only ever reads the server's own lifecycle.
export function isTerminalIngestRow(row) {
  return !PENDING_INGEST_NON_TERMINAL.has(row?.lifecycle);
}
// Per-org, per-browser dismissal list — the same localStorage-preference
// convention MusicChoreography.js already uses for mc.videoLibraryCollapsed/
// mc.routinesRailCollapsed (a try/catch-guarded, JSON-serialised value keyed
// under the "mc." prefix). Deliberately NOT a server call: dismissal changes
// nothing the ingest pipeline owns (see dismissUpload's own comment below), so
// there is nothing to synchronise cross-device and no new backend surface is
// needed for a purely local "stop showing me this" preference.
export function dismissedUploadsStorageKey(orgId) { return `mc.dismissedUploads.${orgId || 'none'}`; }
export function loadDismissedUploadIds(orgId) {
  try {
    const raw = JSON.parse(globalThis.localStorage?.getItem(dismissedUploadsStorageKey(orgId)) || '[]');
    return new Set(Array.isArray(raw) ? raw.filter((id) => typeof id === 'string' && id) : []);
  } catch { return new Set(); }
}
function PendingUploadTile({ row, onRetryUpload, onDismissUpload }) {
  const label = sensibleTitle(row, row?.originalFilename || 'Untitled upload');
  const terminal = isTerminalIngestRow(row);
  const statusLabel = pendingIngestStatusLabel(row);
  return (
    <div
      className={`mc-tile mc-tile-pending mc-tile-pending-${row?.lifecycle || 'admitted'}`}
      data-upload-id={row?.uploadId}
      data-lifecycle={row?.lifecycle}
    >
      <div className="mc-tile-frame">
        <div className="mc-tile-placeholder" aria-hidden="true">{terminal ? '!' : '⏳'}</div>
      </div>
      <div className="mc-pending-tile-meta">
        <span className="mc-pending-tile-name" title={row?.originalFilename || label}>{label}</span>
        {/* T-MCRO-030 Case M / GitLab WebUI #20 requirement 1: a terminal-failed
            entry must say WHY (the server's own failureMessage, never a bare
            "Failed"). role="alert" only for the terminal (failed/expired) states
            so a merely-in-progress "Uploading"/"Processing" tile stays a calm
            role="status" instead of interrupting like an error would. */}
        <span className={`mc-pending-tile-status ${terminal ? 'mc-alert-inline' : ''}`} role={terminal ? 'alert' : 'status'}>
          {statusLabel}
        </span>
        {row?.createdAt && (
          <time className="mc-pending-tile-time mc-muted" dateTime={row.createdAt}>
            {new Date(row.createdAt).toLocaleString()}
          </time>
        )}
        {terminal && (onRetryUpload || onDismissUpload) && (
          <div className="mc-pending-tile-actions">
            {onRetryUpload && (
              <button type="button" className="mc-quiet mc-pending-tile-retry" onClick={onRetryUpload}>
                Try again
              </button>
            )}
            {/* #29: dismissal is view-only — see dismissUpload's own comment on the
                main component for exactly what this does and does not touch. Never
                offered for a non-terminal row (the `terminal &&` guard above). */}
            {onDismissUpload && (
              <button
                type="button"
                className="mc-quiet mc-pending-tile-dismiss"
                aria-label={`Dismiss ${label}`}
                onClick={() => onDismissUpload(row?.uploadId)}
              >
                Dismiss
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// MCRO001-F0053 / T-MCRO-053 (MCRO001-D011 §5.3): "Root > Autumn showcase >
// Grade 3", each segment a clickable link that re-opens the library at that
// level; Root is always first and unclickable-when-current (a link to "where
// you already are" is not a link). `path` is the FULL chain including the
// current folder, root always at index 0 as `{ folderId: null, name: 'Root' }`.
//
// D011 §5.2: there is no dedicated ancestors endpoint in the merged backend
// contract, and the design doc is explicit this is fine for v1 — "the client
// does not need a dedicated 'get ancestors' endpoint... opening a folder the
// client has not seen the ancestors of yet costs at most 5 additional GETs".
// This client takes the simpler, contract-respecting reading of that: the path
// is built purely from client-side navigation (each folder tile the user
// clicks already carries its own {folderId, name}, appended to the trail as
// they go), never from a server-side ancestor walk. It resets to Root on tab
// switch/reload rather than attempting to resolve an arbitrary folder's
// ancestors from scratch — there is no GET-folder-by-id endpoint to do that
// with. See the MR description for why this is flagged as a possible D011
// amendment (a dedicated ancestors/path endpoint) rather than worked around
// with an invented client call.
// MCRO001-D011 §5.3a: `dropTargetIndex`/`onSegment*` are OPTIONAL and default to
// inert — the picker's own internal browse breadcrumb (FolderDestinationPicker,
// below) reuses this exact component for its OWN navigation and must never
// become a drop target itself (there is no drag happening while that modal is
// open); it simply never passes these props, so it renders byte-identical to
// before this amendment.
function FolderBreadcrumb({ path, onNavigate, dropTargetIndex = -1, onSegmentDragEnter, onSegmentDragOver, onSegmentDragLeave, onSegmentDrop }) {
  return (
    <nav className="mc-folder-breadcrumb" aria-label="Folder location">
      <ol>
        {path.map((segment, index) => {
          const isCurrent = index === path.length - 1;
          const isDropTarget = index === dropTargetIndex;
          return (
            <li key={segment.folderId || 'root'}>
              {index > 0 && <span className="mc-folder-breadcrumb-sep" aria-hidden="true">/</span>}
              {isCurrent
                ? <span aria-current="location">{segment.name}</span>
                : (
                  <button
                    type="button"
                    className={`mc-folder-breadcrumb-link ${isDropTarget ? 'is-drop-target' : ''}`}
                    onClick={() => onNavigate(index)}
                    onDragEnter={onSegmentDragEnter && ((event) => onSegmentDragEnter(event, segment, index))}
                    onDragOver={onSegmentDragOver && ((event) => onSegmentDragOver(event, segment, index))}
                    onDragLeave={onSegmentDragLeave && ((event) => onSegmentDragLeave(event, segment, index))}
                    onDrop={onSegmentDrop && ((event) => onSegmentDrop(event, segment, index))}
                  >{segment.name}</button>
                )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// One folder tile: rendered BEFORE media tiles in the same grid (MCRO001-D011
// §5.3), visually distinguished by a folder glyph + child count, reusing the
// existing tile's click/Enter/Space activation pattern rather than a second
// navigation widget. Rename/Move/Archive live in a small accessible menu — the
// SAME role="menu"/role="menuitem" arrow-key-navigable pattern
// CommunityReview.js's ThreadActions already established in this codebase (no
// new menu idiom introduced here).
function FolderTile({
  folder, canManage, onOpen, onRename, onMove, onArchive, renaming, onSubmitRename, onCancelRename,
  // MCRO001-D011 §5.3a: drop-target state/handlers for the drag-to-folder move
  // (T-MCRO-055). All optional — a caller that never wires drag-move (e.g. no
  // onMoveMedia) simply never sets `isDropTarget` and never passes handlers,
  // so this tile behaves exactly as it did before this amendment.
  isDropTarget, onDragEnterMedia, onDragOverMedia, onDragLeaveMedia, onDropMedia,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const nameInputRef = useRef(null);
  const label = folder.name || 'Untitled folder';
  // MCRO001-D011 §5.3a: "12 items"/"Empty" beside the glyph+name — media and
  // subfolders counted together, matching what opening the folder would show.
  // The merged `GET /media/folders` contract (§5.1) does not itself return a
  // count today (verified against choreographyMediaFoldersService.listFolders
  // in the choreo repo) — this renders `folder.itemCount` defensively, the
  // same "prop absent = feature absent" convention this file already applies
  // to onDelete/trackColours/etc., so the tile is correct the instant a
  // caller supplies a real count (MusicChoreography.js already forwards the
  // whole folder object verbatim, so no further WebUI change is needed once
  // the backend adds the field) and never fabricates a count it does not have.
  const itemCountValue = Number(folder?.itemCount);
  const itemCountLabel = Number.isFinite(itemCountValue)
    ? (itemCountValue === 0 ? 'Empty' : `${itemCountValue} item${itemCountValue === 1 ? '' : 's'}`)
    : null;

  useEffect(() => { if (menuOpen) menuRef.current?.querySelector('[role="menuitem"]')?.focus(); }, [menuOpen]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const dismiss = (event) => { if (!menuRef.current?.contains(event.target) && event.target !== triggerRef.current) setMenuOpen(false); };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [menuOpen]);
  useEffect(() => { if (renaming) { nameInputRef.current?.focus(); nameInputRef.current?.select(); } }, [renaming]);

  const keyDownMenu = (event) => {
    const items = [...(menuRef.current?.querySelectorAll('[role="menuitem"]') || [])];
    const index = items.indexOf(document.activeElement);
    if (event.key === 'Escape') { event.preventDefault(); setMenuOpen(false); triggerRef.current?.focus(); }
    else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : event.key === 'ArrowDown' ? (index + 1) % items.length : (index - 1 + items.length) % items.length;
      items[next]?.focus();
    }
  };
  const runMenuAction = (action) => { setMenuOpen(false); action(); };

  if (renaming) {
    const submit = (event) => {
      event.preventDefault();
      const value = nameInputRef.current?.value.trim();
      if (value) onSubmitRename(value);
    };
    return (
      <form className="mc-tile mc-folder-tile mc-folder-tile-renaming" onSubmit={submit} aria-label={`Rename folder ${label}`}>
        <div className="mc-folder-tile-glyph" aria-hidden="true">📁</div>
        <input
          ref={nameInputRef}
          className="mc-folder-rename-input"
          defaultValue={folder.name}
          aria-label="Folder name"
          maxLength={255}
          onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); onCancelRename(); } }}
        />
        <div className="mc-folder-rename-actions">
          <button type="submit" className="mc-quiet">Save</button>
          <button type="button" className="mc-quiet" onClick={onCancelRename}>Cancel</button>
        </div>
      </form>
    );
  }

  return (
    <div
      // MCRO001-D011 §5.3a: the drop-target highlight reuses the EXISTING
      // `.mc-tile.is-selected` coral accent verbatim — no new visual language
      // is introduced for a folder tile's own hover state.
      className={`mc-tile mc-folder-tile ${isDropTarget ? 'is-selected' : ''}`}
      role="button"
      tabIndex={0}
      aria-label={`Folder: ${label}. Enter to open.`}
      onClick={() => onOpen(folder)}
      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(folder); } }}
      onDragEnter={onDragEnterMedia}
      onDragOver={onDragOverMedia}
      onDragLeave={onDragLeaveMedia}
      onDrop={onDropMedia}
      data-folder-id={folder.folderId}
    >
      <div className="mc-folder-tile-glyph" aria-hidden="true">📁</div>
      <span className="mc-folder-tile-name">{label}</span>
      {itemCountLabel && <span className="mc-folder-tile-count">{itemCountLabel}</span>}
      {canManage && (
        <div className="mc-review-actions mc-folder-tile-menu">
          <button
            ref={triggerRef}
            type="button"
            className="mc-review-actions-trigger"
            aria-label={`More actions for folder ${label}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={(event) => { event.stopPropagation(); setMenuOpen((value) => !value); }}
            onKeyDown={(event) => { event.stopPropagation(); if (event.key === 'ArrowDown') { event.preventDefault(); setMenuOpen(true); } }}
          ><span aria-hidden="true">⋯</span></button>
          {menuOpen && (
            <div
              ref={menuRef}
              className="mc-review-actions-menu"
              role="menu"
              aria-label={`Actions for folder ${label}`}
              onKeyDown={keyDownMenu}
              onClick={(event) => event.stopPropagation()}
            >
              <button type="button" role="menuitem" onClick={() => runMenuAction(onRename)}>Rename</button>
              <button type="button" role="menuitem" onClick={() => runMenuAction(onMove)}>Move to folder…</button>
              <button type="button" role="menuitem" onClick={() => runMenuAction(onArchive)}>Archive folder</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// MCRO001-F0053 / T-MCRO-053 (MCRO001-D011 §5.3): the "Move to folder…" picker,
// shared by folder tiles and media tiles alike — v1 is menu-based, never
// drag-and-drop (a deliberate accessibility/effort call recorded in D011 §5.3;
// drag-to-folder is a recorded v2 TODO, D011 §8: "no pointer-drag
// infrastructure exists anywhere in this grid today... a 'Move to folder…'
// entry... is a same-shape, low-effort addition with keyboard/screen-reader
// parity for free"). A small modal dialog rather than a dropdown: choosing a
// destination can mean drilling into an arbitrary depth of the tree, a
// multi-step browse interaction the existing role="menu" idiom (FolderTile's
// own rename/move/archive menu) is not shaped for. Portalled to
// document.body for the same reason ExpandedMedia/AccessDrawer are — no
// ancestor stacking context can trap it at the page bottom.
//
// Depth/cycle checks are NOT precomputed client-side for a move (the resulting
// depth depends on the depth of the subtree being moved, which the client does
// not know) — this surfaces the server's own
// CHOREOGRAPHY_FOLDER_DEPTH_EXCEEDED / CHOREOGRAPHY_FOLDER_CYCLE message
// inline and keeps the dialog open, rather than silently closing on failure.
function FolderDestinationPicker({ title, listFolders, onChoose, onClose, excludeFolderId }) {
  const [path, setPath] = useState([]); // [] = root; else [{ folderId, name }, ...]
  const [children, setChildren] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const closeButtonRef = useRef(null);
  const headingId = useRef(`mc-folder-picker-heading-${Math.random().toString(36).slice(2)}`).current;
  const here = path[path.length - 1] || null;

  useEffect(() => { closeButtonRef.current?.focus(); }, []);
  useEffect(() => {
    const onKeyDown = (event) => { if (event.key === 'Escape') { event.preventDefault(); onClose(); } };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading'); setError('');
    Promise.resolve(listFolders(here?.folderId || null)).then((rows) => {
      if (cancelled) return;
      // Hiding the folder actually being moved (and, one level deep, its
      // direct children — a courtesy, not a substitute for the guard) from its
      // own picker listing is a client-side courtesy so the obviously-
      // nonsensical "move it into itself" choice never even renders. The
      // server's own ancestor-chain cycle guard (MCRO001-D011 §4.3) is what
      // actually prevents every cycle shape, including the ones this filter
      // does not catch (e.g. a deeper descendant) — surfaced via submitError
      // below if reached anyway.
      setChildren((rows || []).filter((row) => !excludeFolderId || folderKey(row) !== excludeFolderId));
      setStatus('ready');
    }).catch((cause) => {
      if (cancelled) return;
      setError(libraryErrorMessage(cause, 'Could not load folders.')); setStatus('error');
    });
    return () => { cancelled = true; };
  }, [listFolders, here, excludeFolderId]);

  const handleChoose = async () => {
    setSubmitting(true); setSubmitError('');
    try {
      // Second argument (the destination's display name) is new for T-MCRO-055's
      // selection-bar/drag-move confirmation copy ("Moved 3 videos to Warm-ups").
      // Backward compatible: the existing single-folder/single-media `onChoose`
      // callers below take only the first argument and ignore the rest.
      await onChoose(here?.folderId || null, here?.name || 'Root');
      onClose();
    } catch (cause) {
      setSubmitError(libraryErrorMessage(cause, 'Could not move there.'));
    } finally {
      setSubmitting(false);
    }
  };

  const overlay = (
    <div className="mc-overlay">
      <section
        className="mc-dialog mc-folder-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onClick={(event) => event.stopPropagation()}
      >
        <span className="mc-kicker">{title}</span>
        <h2 id={headingId}>Choose a folder</h2>
        <FolderBreadcrumb
          path={[{ folderId: null, name: 'Root' }, ...path]}
          onNavigate={(index) => setPath(index === 0 ? [] : path.slice(0, index))}
        />
        {status === 'loading' && <p className="mc-muted" role="status">Loading folders…</p>}
        {status === 'error' && <p className="mc-alert" role="alert">{error}</p>}
        {status === 'ready' && (
          <ul className="mc-folder-picker-list">
            {children.length === 0 && <li className="mc-muted mc-folder-picker-empty">No subfolders here.</li>}
            {children.map((child) => (
              <li key={folderKey(child)}>
                <button type="button" className="mc-quiet mc-folder-picker-row" onClick={() => setPath([...path, { folderId: child.folderId, name: child.name }])}>
                  <span aria-hidden="true">📁</span> {child.name}
                </button>
              </li>
            ))}
          </ul>
        )}
        {submitError && <p className="mc-alert" role="alert">{submitError}</p>}
        <div className="mc-dialog-actions">
          <button type="button" className="mc-quiet" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="button" ref={closeButtonRef} className="mc-primary" disabled={submitting} onClick={handleChoose}>
            {submitting ? 'Moving…' : `Move to ${here ? `"${here.name}"` : 'Root'}`}
          </button>
        </div>
      </section>
    </div>
  );
  return typeof document !== 'undefined' && document.body ? createPortal(overlay, document.body) : overlay;
}

// MCRO001-F0040 / T-MCRO-040 Case B: the recycle bin.
//
// Before this existed, `archived_at` was a database column and nothing more —
// nothing listed archived media and nothing restored it, so from the application a
// delete was irreversible. This is that missing surface: a separate view over the
// same library pane, never mixed into the normal grid or any canvas.
function TrashView({ trash, onRestore, onClose, restoringIds }) {
  const media = trash?.media || [];
  const retentionDays = trash?.retentionDays;
  return (
    <div className="mc-library-trash" role="region" aria-label="Trash">
      <header className="mc-library-head">
        <div className="mc-library-title">
          <span className="mc-kicker">Recently deleted</span>
          <h2>Trash</h2>
        </div>
        <div className="mc-library-controls">
          <button type="button" className="mc-quiet" onClick={onClose}>Back to library</button>
        </div>
      </header>
      {trash?.error
        ? <div className="mc-alert" role="alert">{trash.error}</div>
        : trash?.loading
          ? <p className="mc-empty" role="status">Loading Trash…</p>
          : media.length === 0
            ? <p className="mc-empty">Trash is empty. Deleted videos appear here{Number.isFinite(retentionDays) ? ` for ${retentionDays} days` : ''} and can be restored until then.</p>
            : (
              <>
                <p className="mc-library-note mc-muted">
                  Deleted videos stay here for {retentionDays} days, then they and their stored
                  video are permanently deleted. Restore returns a video to your library and to
                  the canvases it was on.
                </p>
                <ul className="mc-trash-list">
                  {media.map((entry) => {
                    const key = String(entry.mediaId);
                    const label = sensibleTitle(entry);
                    return (
                      <li key={key} className="mc-trash-row" data-media-id={key}>
                        <span className="mc-trash-glyph" aria-hidden="true">{mediaIsImage(entry) ? '▧' : '▶'}</span>
                        <span className="mc-trash-title">{label}</span>
                        {/* The remaining time comes from the SERVER's computed
                            purgeAfter/daysRemaining, never re-derived here, so the
                            UI can never disagree with the job about when something
                            actually disappears. */}
                        <span className="mc-trash-remaining mc-muted">
                          {entry.expired
                            ? 'Due for permanent deletion'
                            : `${entry.daysRemaining} day${entry.daysRemaining === 1 ? '' : 's'} left`}
                        </span>
                        <button
                          type="button"
                          className="mc-quiet"
                          disabled={restoringIds.has(key)}
                          onClick={() => onRestore(entry)}
                        >{restoringIds.has(key) ? 'Restoring…' : 'Restore'}</button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
    </div>
  );
}

// The expanded full-size view: a large authorised player plus in-place editable
// metadata, presented as a MODAL OVERLAY (role="dialog", aria-modal) portalled to
// document.body — NOT an inline card in document flow. Before 2026-08-06 this
// rendered as a `<section class="mc-expanded">` appended after the grid, so the
// full-size player appeared as a small card tucked at the very BOTTOM of the page
// instead of a large centred player over a dimmed backdrop (MCRO001-F0006 /
// T-MCRO-006 presentation). The overlay now: fills the viewport with a dimmed
// backdrop, centres a large video/image (max 92vw × 90vh, aspect preserved), shows
// native <video controls> so the browser's own fullscreen still works inside it,
// and closes on the X button, Escape, or a backdrop (scrim) click — but never on a
// click on the media itself. Body scroll is locked while open and focus is trapped.
//
// Saves on explicit Save or field blur through onSaveMetadata (the choreo
// PATCH /media/:mediaId endpoint). Read-only when the caller cannot edit.
function ExpandedMedia({ item, orgId, mintStreamUrl, canEdit, onSaveMetadata, onClose, onUseInRoutine, onAddToCanvas, onOpenShare, restoreFocusRef, onMoveToFolder }) {
  const image = mediaIsImage(item);
  // T-MCRO-032 case H: the expanded/full player streams the `content` variant
  // (full rendition) for both video and image media — real streaming, not blob.
  // Images keep the plain cached mint; video uses the renewing hook (M004-F0779 / WebUI #251).
  const imagePlayer = useStreamUrl(mintStreamUrl, orgId, item, 'content', image);
  const mediaId = mediaKey(item);
  const videoPlayer = useRefreshingStream(() => { invalidateStreamUrl(orgId, mediaId, 'content'); return resolveStreamUrl(mintStreamUrl, orgId, mediaId, 'content'); }, [orgId, mediaId], { active: !image });
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const headingId = useRef(`mc-expanded-heading-${Math.random().toString(36).slice(2)}`).current;

  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release
  // (both mousedown and mouseup on the scrim itself); a text-selection drag from
  // an editable field (title/tags/teaching point below) to outside the dialog
  // must not close it.
  const scrimDismissHandlers = useDismissOnOutsideClick(onClose);

  // Move focus into the dialog (its close button) on open, and restore it to
  // whatever triggered the open once the dialog unmounts — standard dialog
  // behaviour, matching AccessDrawer.
  useEffect(() => {
    if (closeRef.current) closeRef.current.focus();
  }, []);
  useEffect(() => {
    const trigger = restoreFocusRef?.current;
    return () => { if (trigger && typeof trigger.focus === 'function') trigger.focus(); };
    // The instance is remounted per item via its `key` in the parent, so this only
    // ever needs to run once per mount.
  }, []); // eslint-disable-line

  // Escape closes regardless of which element inside has focus; Tab is trapped so
  // focus can never leave the modal while it is open.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
      if (event.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusable = Array.from(root.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) { event.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !root.contains(active))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  // A true overlay must not let the page scroll behind it.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);
  const initial = useMemo(() => ({
    title: sensibleTitle(item, ''),
    teachingPoint: item?.teachingPoint || item?.description || '',
    tags: (item?.tags || []).join(', '),
  }), [item]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  useEffect(() => { setForm(initial); setNotice(''); }, [initial]);

  const dirty = form.title !== initial.title || form.teachingPoint !== initial.teachingPoint || form.tags !== initial.tags;

  const persist = useCallback(async () => {
    if (!canEdit || !dirty || saving) return;
    const title = form.title.trim();
    if (!title) { setNotice('A title is required.'); return; }
    setSaving(true); setNotice('');
    try {
      await onSaveMetadata(item, {
        title,
        teachingPoint: form.teachingPoint.trim() || null,
        tags: form.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      });
      setNotice('Saved.');
    } catch (error) {
      setNotice(error?.message || 'The changes could not be saved.');
    } finally { setSaving(false); }
  }, [canEdit, dirty, saving, form, item, onSaveMetadata]);

  const overlay = (
    <div className="mc-expanded-overlay">
      {/* Dimmed backdrop / scrim: click-outside-to-close. The dialog panel below
          stops propagation, and the media element is inside that panel, so a click
          on the video/image never bubbles here — only a click on the dimmed area
          around it closes. */}
      <div className="mc-expanded-scrim" aria-hidden="true" {...scrimDismissHandlers} />
      <section
        ref={dialogRef}
        className="mc-expanded"
        role="dialog"
        aria-modal="true"
        aria-label={`Expanded media: ${sensibleTitle(item)}`}
        aria-labelledby={headingId}
        onClick={(event) => event.stopPropagation()}
      >
        <span id={headingId} className="mc-visually-hidden">{`Expanded media: ${sensibleTitle(item)}`}</span>
        {/* Always-visible close affordance, top-right of the large player. */}
        <button
          type="button"
          ref={closeRef}
          className="mc-expanded-close"
          aria-label="Close expanded media"
          onClick={onClose}
        ><span aria-hidden="true">✕</span></button>
        <div className="mc-expanded-player">
          {image
            ? (imagePlayer.status === 'ready'
              ? <img src={imagePlayer.url} alt={sensibleTitle(item, 'Media')} />
              : imagePlayer.status === 'error'
                ? <div className="mc-empty" role="alert">{imagePlayer.error}</div>
                : <div className="mc-empty" role="status">Loading media…</div>)
            : videoPlayer.status === 'failed'
              ? (
                <div className="mc-empty" role="alert">
                  <span data-testid="stream-refresh-failed">{videoPlayer.error}</span>
                  <button type="button" data-testid="stream-refresh-retry" onClick={() => videoPlayer.retry()}>Retry</button>
                </div>
              )
              : videoPlayer.src
                ? <video src={videoPlayer.src} {...videoPlayer.videoProps} controls autoPlay={false} preload="metadata" aria-label={`Player for ${sensibleTitle(item, 'media')}`} />
                : <div className="mc-empty" role="status">Loading media…</div>}
        </div>
        <div className="mc-expanded-meta">
          <div className="mc-expanded-head">
            <span className="mc-kicker">{kindLabel(item)}</span>
            <div className="mc-expanded-actions">
              {/* WebUI issue #30 / M001-D023 §7 §12.1: the video library's own
                  Share management entry point — same conformant panel the
                  Sheet uses (ResourceSharePanel), resourceType 'choreo_media'.
                  Gated on canEdit like the metadata fields above: managing who
                  can access this item needs the same authority as editing it. */}
              {canEdit && onOpenShare && <button type="button" className="mc-quiet" onClick={(event) => onOpenShare(item, event.currentTarget)}>Share</button>}
              {onAddToCanvas && <button type="button" className="mc-quiet" onClick={() => onAddToCanvas([item])}>Add to canvas</button>}
              {onUseInRoutine && <button type="button" className="mc-quiet" onClick={() => onUseInRoutine(item)}>Timing editor</button>}
              {/* MCRO001-F0053 / T-MCRO-053 (MCRO001-D011 §5.3): "Move to
                  folder…" menu action — lives here, not as a tile-corner icon;
                  see MediaTile's own comment for why. Same authority as this
                  whole expanded view's edit fields (canEdit), so it is only
                  offered when onMoveToFolder is wired — never shown-and-broken. */}
              {onMoveToFolder && <button type="button" className="mc-quiet" onClick={() => onMoveToFolder(item)}>Move to folder…</button>}
            </div>
          </div>
          {canEdit ? (
            <div className="mc-expanded-fields" onBlur={persist}>
              <label className="mc-field"><span>Title</span><input aria-label="Media title" value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} /></label>
              <label className="mc-field"><span>Teaching point</span><textarea aria-label="Teaching point" rows={2} value={form.teachingPoint} onChange={(event) => setForm((prev) => ({ ...prev, teachingPoint: event.target.value }))} /></label>
              <label className="mc-field"><span>Tags</span><input aria-label="Tags (comma separated)" value={form.tags} onChange={(event) => setForm((prev) => ({ ...prev, tags: event.target.value }))} placeholder="comma, separated" /></label>
              <div className="mc-expanded-save"><button type="button" className="mc-primary" onClick={persist} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save'}</button>{notice && <span className="mc-notice" role="status">{notice}</span>}</div>
            </div>
          ) : (
            <div className="mc-expanded-fields mc-expanded-readonly">
              <h3>{sensibleTitle(item)}</h3>
              {(item?.teachingPoint || item?.description) && <p className="mc-muted">{item.teachingPoint || item.description}</p>}
              {(item?.tags || []).length > 0 && <small className="mc-muted">{item.tags.join(' · ')}</small>}
            </div>
          )}
        </div>
      </section>
    </div>
  );
  // Portal to document.body so no ancestor stacking context / overflow can trap the
  // overlay at the bottom of the page (the exact defect this replaces).
  return typeof document !== 'undefined' && document.body
    ? createPortal(overlay, document.body)
    : overlay;
}

export default function MediaLibraryGrid({
  items = [],
  itemsStatus = 'ready',
  itemsError = '',
  orgId,
  canUpload = false,
  canEdit = false,
  // Retained for backward compatibility only: MusicChoreography.js still passes
  // this (blob-fetch) prop, but nothing in this component consumes it any more
  // now that tiles + the expanded player stream signed URLs instead (T-MCRO-032
  // cases G/H/I). An unused prop is a harmless no-op — no call-site change needed.
  getContent, // eslint-disable-line no-unused-vars
  // WebUI issue #30 / M001-D023 §7 §12.1: the video library's Share management
  // surface. (The read-only getAccess/getAccessExplain drawer props were
  // retired by M004-F0664 / #39.) Each is `(orgId, mediaId, ...) =>
  // Promise<axios response>`, unbound to any one item — this component supplies orgId + the open item's key when it
  // calls them. Absent (a caller that hasn't wired them in) means no Share
  // button renders, same "prop absent = feature absent" convention as
  // onMoveToFolder etc. elsewhere in this file.
  getMediaSharingAccess,
  replaceMediaSharingAccess,
  searchMediaSharingPrincipals,
  createMediaSharingLink,
  deleteMediaSharingLink,
  retryMediaSharingInvitation,
  // ResourceAccessManager's `api.getAccessExplain`
  // contract (ResourceSharePanel.js) expects the raw axios-shaped response —
  // same contract every other sharingApi function here uses — so this is the
  // unwrapped call, not a re-use of getAccessExplain under a new name.
  getMediaSharingAccessExplain,
  onSaveMetadata,
  onFilesSelected,
  onUseInRoutine,
  onAddToCanvas,
  // Mints a short-lived, variant-locked signed stream URL: (orgId, mediaId, variant)
  // => Promise<{ data: { url, expiresAt } }>. Defaults to the real choreo API call
  // (imported directly, since MusicChoreography.js — out of scope to edit — never
  // passes this prop in production); tests inject a mock override here.
  mintStreamUrl = defaultMintStreamUrl,
  // MCRO001-F0040 / T-MCRO-040 Case D: the configured Trash retention window, from
  // capabilities().mediaRetention.trashRetentionDays. It is a prop rather than a
  // constant because the owner may change CHOREO_MEDIA_TRASH_RETENTION_DAYS, and a
  // hardcoded 30 in the confirmation text would then be a lie. Falls back to 30 only
  // when capabilities has not resolved yet.
  trashRetentionDays = 30,
  // MCRO001-F0039 / T-MCRO-039 Case G: called after the actor confirms a library
  // delete. Absent (or canEdit false) means no delete control is rendered at all.
  onDeleteMedia,
  // MCRO001-F0040 / T-MCRO-040 Cases B and C.
  listTrash,
  onRestoreMedia,
  confirm = (message) => (typeof window !== 'undefined' && typeof window.confirm === 'function' ? window.confirm(message) : true),
  // MCRO001-F0052: { [mediaId]: colour } for every video that currently has a
  // Canvas demonstration timing rail, built from the SAME shared media-keyed source
  // the rail colours its lines with (mediaTrackColour.js, from the demonstration
  // candidates in MusicChoreography.js). A tile whose mediaId appears here borders in
  // that colour so it matches its rail; a video with no rail is absent and keeps its
  // ordinary border. Absent/empty (no rail yet, or a caller that never wired it)
  // leaves every tile exactly as before.
  trackColours = {},
  // MCRO001-F0030 / T-MCRO-030 Case M (GitLab WebUI #20): the caller's own
  // video-ingest sessions that are not (yet, or ever) real media rows — choreo's
  // GET /video-ingests, `[{ uploadId, lifecycle, originalFilename, title,
  // failureMessage, createdAt, ... }]`. Merged into the same grid as `items`
  // (see `filteredPending`/`hasAnyMediaAtAll` below) so an admitted/uploading/
  // processing/failed/expired upload is always visible, never indistinguishable
  // from a deletion. Absent/empty behaves exactly as before this Feature existed.
  pendingUploads = [],
  // MCRO001-F0053 / T-MCRO-053 (MCRO001-D011 §5.3): the nested media-library
  // folder tree. `folders` are the LIVE direct child folders of the currently
  // open location (server response shape: [{ folderId, parentFolderId, name,
  // createdBy, createdAt }], matching choreographyMediaFoldersService.listFolders
  // exactly). `breadcrumb` is the full path from root to the open folder
  // INCLUSIVE, root always `{ folderId: null, name: 'Root' }` at index 0 — see
  // FolderBreadcrumb's own comment for why this is built client-side rather
  // than from a server ancestor walk. Absent/empty `folders` + the default
  // root-only `breadcrumb` renders exactly as this component did before this
  // Feature existed (no folder chrome for a caller that never wires this in).
  folders = [],
  foldersStatus = 'ready',
  foldersError = '',
  breadcrumb = [{ folderId: null, name: 'Root' }],
  // Gates folder create/rename/move/archive/restore — the SAME
  // choreography_video_manage_all bar the backend enforces (MCRO001-D011 §5.1
  // Case K), which is narrower than `canEdit` (manage_all OR upload_own): an
  // upload-own-only actor can edit/delete their own media but must never see a
  // folder-management control that would only 403.
  canManageFolders = false,
  onOpenFolder,
  onCreateFolder,
  onRenameFolder,
  onMoveFolder,
  onArchiveFolder,
  onRestoreFolder,
  // (parentFolderIdOrNull) => Promise<folderRows> — powers the "Move to
  // folder…" destination picker's own independent browse state, kept separate
  // from the grid's own `folders`/`breadcrumb` so browsing a picker never
  // disturbs the page behind it.
  listFoldersForPicker,
  // MCRO001-D011 §5.1: PATCH /media/:mediaId already accepts folderId — same
  // authority as every other tile edit action, so this is offered whenever
  // canEdit is (no separate gate).
  onMoveMedia,
}) {
  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [expandedId, setExpandedId] = useState('');
  const [quickPlayId, setQuickPlayId] = useState('');
  const [dragOverDepth, setDragOverDepth] = useState(0);
  // MCRO001-F0039 / F0040: library delete + Trash.
  const [deletingIds, setDeletingIds] = useState(() => new Set());
  const [restoringIds, setRestoringIds] = useState(() => new Set());
  const [libraryNotice, setLibraryNotice] = useState('');
  const [showTrash, setShowTrash] = useState(false);
  const [trash, setTrash] = useState(null);
  // GitLab WebUI #29: uploadIds the actor has chosen to stop seeing, scoped to
  // this org and this browser (see loadDismissedUploadIds' own comment). Reset
  // whenever orgId changes so switching orgs never carries a dismissal list
  // that belongs to a different org's uploads into view.
  const [dismissedUploadIds, setDismissedUploadIds] = useState(() => loadDismissedUploadIds(orgId));
  useEffect(() => { setDismissedUploadIds(loadDismissedUploadIds(orgId)); }, [orgId]);
  // Dismissal is a pure client-side view filter: it does not call any API, does
  // not touch choreography_media, does not touch the video-ingest session the
  // server still owns (no delete/cancel is sent), and does not affect the next
  // GET /video-ingests poll — the row keeps existing server-side, with its own
  // real lifecycle, for as long as choreo's own retention sweep keeps it. All
  // this does is add the id to a local "don't show me this again" list, so it
  // stops rendering in THIS browser's grid. Guarded to terminal rows only —
  // see isTerminalIngestRow's comment for why "terminal" is defined from the
  // server lifecycle, never invented client-side.
  const dismissUpload = useCallback((uploadId) => {
    if (!uploadId) return;
    const row = pendingUploads.find((entry) => entry?.uploadId === uploadId);
    if (!isTerminalIngestRow(row)) return;
    setDismissedUploadIds((current) => {
      if (current.has(uploadId)) return current;
      const next = new Set(current);
      next.add(uploadId);
      try { globalThis.localStorage?.setItem(dismissedUploadsStorageKey(orgId), JSON.stringify(Array.from(next))); } catch { /* Storage is optional. */ }
      return next;
    });
  }, [orgId, pendingUploads]);
  // MCRO001-F0053 / T-MCRO-053: folder UI interaction state.
  const [showNewFolderForm, setShowNewFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [folderNotice, setFolderNotice] = useState('');
  const [renamingFolderId, setRenamingFolderId] = useState('');
  // The picker is shared by folder tiles ("Move to folder…" in FolderTile's
  // menu) and media tiles (MediaTile's own "Move to folder…" icon) — exactly
  // one of `movingFolder`/`movingMedia` is set at a time, deciding both the
  // picker's title and which onChoose handler it calls.
  const [movingFolder, setMovingFolder] = useState(null);
  const [movingMedia, setMovingMedia] = useState(null);
  // MCRO001-D011 §5.3a / T-MCRO-055: the SEPARATE non-drag "Move to…" path for
  // the whole current selection (selection-bar button), kept distinct from
  // `movingMedia` above (that one is the existing, unchanged, single-item
  // per-tile "Move to folder…" menu action, which still throws on failure to
  // keep its own dialog open and show `submitError` inline — §5.3's original,
  // unmodified contract). A multi-item move is never atomic (no batch-move
  // endpoint, §8 Owned TODO) so it must never throw through the picker; it
  // always resolves and reports success/partial-failure honestly via
  // `folderNotice` instead (see `moveMediaItems` below).
  const [movingSelection, setMovingSelection] = useState(null); // array of media items, or null
  // Drop-target hover state for the drag-to-folder move (§5.3a) — which
  // folder tile / breadcrumb segment is currently highlighted, if any.
  const [dropTargetFolderId, setDropTargetFolderId] = useState('');
  const [dropTargetBreadcrumbIndex, setDropTargetBreadcrumbIndex] = useState(-1);
  // MCRO001-F0158 / T-MCRO-158 (MCRO001-D011 §5.3a, 2026-09-03 — choreo issue
  // #51 reclassification, jobs 176219/176317): the drag gesture is guarded
  // against a mid-gesture refetch.
  //
  // The defect, browser-faithfully: a folder open (or any `load()`/
  // `loadFolders()` cycle) does NOT clear the listing while its fetch is in
  // flight (MusicChoreography's `load` only `setItems` when the response
  // lands), so the listing can swap under a pressed pointer between the
  // physical press and the browser's `dragstart`. The drag payload was then
  // derived from whichever render pass happened to back the node under the
  // pointer at that instant — a user can press clip A and move clip B (the
  // 176317 :979 signature: exactly one PATCH for the OTHER mediaId), or the
  // pressed tile can unmount mid-press so no payload exists at all (the
  // 176219 no-delivery signature).
  //
  // Two halves, both required:
  //   1. `dragGestureRef` — the payload ids are captured at the PHYSICAL
  //      PRESS (pointerdown) from that render pass, and `onDragMediaStart`
  //      anchors them to the dragged node's own `data-media-id`. The drop
  //      consumes that payload; it never re-derives ids from render state.
  //   2. `gestureSnapshot` — from the press until the gesture ends, the grid
  //      renders the FROZEN view (items/folders/breadcrumb/pendingUploads as
  //      they were at the press), so a refetch resolving mid-gesture cannot
  //      re-render the listing or unmount the pressed tile. The snapshot is
  //      a DEFERRAL, not a discard: when the gesture ends the live props
  //      render as normal, so the refetch is applied, never lost. Selection
  //      pruning keeps running on the LIVE items (self-healing selection is
  //      not frozen); only the rendered listing is.
  const [gestureSnapshot, setGestureSnapshot] = useState(null);
  const dragGestureRef = useRef(null);
  const dragStartedRef = useRef(false);
  // The frozen view (half 2). Everything the GRID renders — the media listing,
  // the folder band, the breadcrumb and the pending-upload band — reads these
  // while a gesture is active; the live props take over again the instant the
  // gesture ends. Search (`query`), selection and Trash stay live: they cannot
  // swap a listing under a pressed pointer mid-gesture, and selection pruning
  // (below) deliberately keeps running on the LIVE items.
  const viewItems = gestureSnapshot ? gestureSnapshot.items : items;
  const viewFolders = gestureSnapshot ? gestureSnapshot.folders : folders;
  const viewBreadcrumb = gestureSnapshot ? gestureSnapshot.breadcrumb : breadcrumb;
  const viewPendingUploads = gestureSnapshot ? gestureSnapshot.pendingUploads : pendingUploads;
  // A dismissed row only ever stays hidden while the SERVER still reports it
  // terminal. If the same uploadId were ever reported non-terminal again (it
  // never should be, given the pipeline's own monotonic lifecycle, but this is
  // the same defensive posture choreo #19 taught: never trust a client-side
  // "this is permanently dead" once the server disagrees) it reappears rather
  // than staying hidden.
  const undismissedPendingUploads = useMemo(
    () => viewPendingUploads.filter((row) => !(dismissedUploadIds.has(row?.uploadId) && isTerminalIngestRow(row))),
    [viewPendingUploads, dismissedUploadIds],
  );
  const newFolderInputRef = useRef(null);
  useEffect(() => { if (showNewFolderForm) newFolderInputRef.current?.focus(); }, [showNewFolderForm]);
  // M004-F0664 (#39): the Share panel is the ONE access surface per video;
  // the read-only Access drawer was retired.
  const shareTriggerRef = useRef(null);
  const [shareId, setShareId] = useState('');
  const openShare = useCallback((item, triggerElement) => {
    shareTriggerRef.current = triggerElement || null;
    setShareId(mediaKey(item));
  }, []);
  const shareItem = useMemo(() => viewItems.find((item) => mediaKey(item) === shareId) || null, [viewItems, shareId]);
  const sharingApi = useMemo(() => (shareId ? {
    getAccess: () => getMediaSharingAccess(orgId, shareId),
    replaceAccess: (body, idem) => replaceMediaSharingAccess(orgId, shareId, body, idem),
    searchPrincipals: (query, types) => searchMediaSharingPrincipals(orgId, shareId, query, types),
    createLink: (body, idem) => createMediaSharingLink(orgId, shareId, body, idem),
    deleteLink: (body, idem) => deleteMediaSharingLink(orgId, shareId, body, idem),
    retryInvitation: (invitationId, body) => retryMediaSharingInvitation(orgId, shareId, invitationId, body),
    getAccessExplain: (subjectUuid) => getMediaSharingAccessExplain(orgId, shareId, subjectUuid),
  } : null), [createMediaSharingLink, deleteMediaSharingLink, getMediaSharingAccessExplain, getMediaSharingAccess, orgId, replaceMediaSharingAccess, retryMediaSharingInvitation, searchMediaSharingPrincipals, shareId]);
  // Whichever element triggered the expanded player's open (a tile's ⛶ expand
  // icon, or the tile body) so focus can be restored to it when the overlay
  // closes — standard dialog focus-return behaviour.
  const expandTriggerRef = useRef(null);
  const openExpanded = useCallback((item, triggerElement) => {
    expandTriggerRef.current = triggerElement || null;
    setExpandedId(mediaKey(item));
  }, []);
  const lastToggledRef = useRef('');
  const filePickerRef = useRef(null);
  const canDropUpload = canUpload && typeof onFilesSelected === 'function';

  const filtered = useMemo(() => {
    const needle = query.toLowerCase().trim();
    if (!needle) return viewItems;
    return viewItems.filter((item) => [item?.title, item?.teachingPoint, item?.description, ...(item?.tags || [])]
      .some((value) => String(value || '').toLowerCase().includes(needle)));
  }, [viewItems, query]);

  // MCRO001-F0030 / T-MCRO-030 Case M: the same search box also reaches pending
  // uploads, by title or original filename — a search for "wedding" should not
  // hide an in-progress "wedding_recital.mp4" just because it has no title yet.
  // #29: filters from undismissedPendingUploads, not the raw prop, so a
  // dismissed terminal attempt stays hidden through search too.
  const filteredPending = useMemo(() => {
    const needle = query.toLowerCase().trim();
    if (!needle) return undismissedPendingUploads;
    return undismissedPendingUploads.filter((row) => [row?.title, row?.originalFilename]
      .some((value) => String(value || '').toLowerCase().includes(needle)));
  }, [undismissedPendingUploads, query]);

  // Keep selection/expansion referencing only media that still exists.
  useEffect(() => {
    const present = new Set(items.map(mediaKey));
    setSelectedIds((prev) => { const next = new Set([...prev].filter((id) => present.has(id))); return next.size === prev.size ? prev : next; });
    setExpandedId((prev) => (prev && present.has(prev) ? prev : ''));
    setQuickPlayId((prev) => (prev && present.has(prev) ? prev : ''));
  }, [items]);

  const toggleSelect = useCallback((item, event) => {
    const key = mediaKey(item);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (event?.shiftKey && lastToggledRef.current) {
        const ids = filtered.map(mediaKey);
        const from = ids.indexOf(lastToggledRef.current);
        const to = ids.indexOf(key);
        if (from !== -1 && to !== -1) {
          const [lo, hi] = from < to ? [from, to] : [to, from];
          for (let i = lo; i <= hi; i += 1) next.add(ids[i]);
          return next;
        }
      }
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    lastToggledRef.current = key;
  }, [filtered]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const dragIdsFor = useCallback((item) => {
    const key = mediaKey(item);
    // Dragging a tile that is part of the selection carries the whole selection;
    // dragging an unselected tile carries just that one.
    if (selectedIds.has(key) && selectedIds.size > 0) return [...selectedIds];
    return [key];
  }, [selectedIds]);

  // T-MCRO-158 half 1: the gesture's payload is frozen at the physical press —
  // the ids the actor actually grabbed, from the render pass that received the
  // press — and half 2 freezes the rendered view for the gesture's lifetime.
  const beginDragGesture = useCallback((item) => {
    if (!item) return;
    dragGestureRef.current = { orgId, mediaIds: dragIdsFor(item) };
    dragStartedRef.current = false;
    setGestureSnapshot({ items, folders, breadcrumb, pendingUploads });
  }, [dragIdsFor, orgId, items, folders, breadcrumb, pendingUploads]);

  const endDragGesture = useCallback(() => {
    dragGestureRef.current = null;
    dragStartedRef.current = false;
    setGestureSnapshot(null);
  }, []);

  // A press that ends WITHOUT a drag (a plain click) must unfreeze the view —
  // the release can land anywhere in the page, so this listens at the document
  // (capture) for exactly that case; a real drag unfreezes via dragend/drop
  // below, and a mid-drag pointerup (never delivered by a native HTML5 drag,
  // but guarded against anyway) is ignored via dragStartedRef.
  useEffect(() => {
    if (!gestureSnapshot) return undefined;
    const onDocumentPointerUp = () => { if (!dragStartedRef.current) endDragGesture(); };
    document.addEventListener('pointerup', onDocumentPointerUp, true);
    return () => document.removeEventListener('pointerup', onDocumentPointerUp, true);
  }, [gestureSnapshot, endDragGesture]);

  // T-MCRO-055 / choreo #51 (WebUI job 204084, 2026-09-07 attempt 1): the THIRD
  // way a gesture ends. The guards above cover dragend, drop and the
  // no-drag pointerup — but a drag session can die WITHOUT the page ever
  // receiving any of them (the browser's own input race during the gesture:
  // CI's video shows the drop-target highlight still pixel-identically lit a
  // full second after the release, no PATCH, no notice, and a page that never
  // changed again for the entire 30s notice wait). When that happens
  // gestureSnapshot stays set and EVERY subsequent refetch is deferred
  // forever — the pane silently stops updating until a reload. A live session
  // cannot be this silent: the spec mandates periodic `drag` (source) and
  // `dragover` (current target) events at ≤350ms intervals even for a held
  // -still pointer, so document-level drag-event silence past
  // DRAG_LIVENESS_SILENCE_BOUND_MS (several missed beats) is proof the
  // session is dead. Ending the gesture here applies the deferred refetch
  // exactly as any other end does — deferred, never discarded. A gesture
  // whose bind was lost still moves nothing and claims nothing (the D011
  // §5.3a item 6 semantics this file already states); this bound fixes the
  // freeze, it does not reconstruct a drop the browser never delivered. The
  // press phase (pointerdown before dragstart) is intentionally NOT bounded —
  // a long deliberate press keeps its freeze and is already covered by the
  // pointerup handler above.
  useEffect(() => {
    if (!gestureSnapshot) return undefined;
    let lastBeat = Date.now();
    const beat = () => { lastBeat = Date.now(); };
    document.addEventListener('drag', beat, true);
    document.addEventListener('dragover', beat, true);
    const timer = setInterval(() => {
      if (dragStartedRef.current && Date.now() - lastBeat > DRAG_LIVENESS_SILENCE_BOUND_MS) endDragGesture();
    }, DRAG_LIVENESS_TICK_MS);
    return () => {
      clearInterval(timer);
      document.removeEventListener('drag', beat, true);
      document.removeEventListener('dragover', beat, true);
    };
  }, [gestureSnapshot, endDragGesture]);

  // T-MCRO-158 half 1, at dragstart: the payload's anchor is the node the
  // browser says is being dragged — the event target's own `data-media-id` —
  // never a re-derivation from whatever render state is current when the event
  // lands. The press-time snapshot is used only when it agrees with that node
  // (it normally does — the snapshot exists precisely so the node cannot be
  // swapped mid-gesture); on any disagreement the DRAGGED NODE wins, so what
  // moves is always what the drag ghost shows, never a mix.
  const onDragMediaStart = useCallback((event, item) => {
    dragStartedRef.current = true;
    const targetId = event?.currentTarget?.getAttribute?.('data-media-id') || '';
    const gesture = dragGestureRef.current;
    const ids = gesture && (!targetId || gesture.mediaIds.includes(targetId))
      ? gesture.mediaIds
      : dragIdsFor({ mediaId: targetId || mediaKey(item) });
    const payload = JSON.stringify({ orgId: gesture?.orgId || orgId, mediaIds: ids });
    try {
      event.dataTransfer.setData(DRAG_MIME, payload);
      event.dataTransfer.setData('text/plain', ids.join(','));
      event.dataTransfer.effectAllowed = 'copy';
    } catch { /* jsdom dataTransfer is limited; the payload shape is what matters. */ }
  }, [dragIdsFor, orgId]);

  // T-MCRO-158: begin/end the guarded drag gesture. `beginDragGesture` freezes
  // the payload ids AND the rendered listing at the press; `endDragGesture`
  // (dragend here, drop handlers, or the document pointerup for a press that
  // never became a drag) hands the view back to the live props, so a refetch
  // that landed mid-gesture is applied then — deferred, never discarded.
  const onTileGesturePointerDown = useCallback((item, event) => {
    if (event && event.button !== undefined && event.button !== 0) return;
    beginDragGesture(item);
  }, [beginDragGesture]);
  const onTileGestureDragEnd = useCallback(() => { endDragGesture(); }, [endDragGesture]);

  // Whole-panel drop target for INCOMING local files (Google-Drive-style
  // upload, M004-F0396). This is distinct from the drag SOURCE above, which
  // carries existing tiles OUT toward a consumer surface (M004-F0392): a drag
  // that carries our own DRAG_MIME payload is an outgoing attach-drag, never
  // an incoming upload, so it is explicitly ignored here and never starts an
  // upload (T-M004-0017's "upload-in and attach-out never cross-trigger").
  const isIncomingFileDrag = (event) => {
    const types = event?.dataTransfer?.types;
    if (!types) return false;
    const list = Array.from(types);
    return list.includes('Files') && !list.includes(DRAG_MIME);
  };

  const onPanelDragOver = useCallback((event) => {
    if (!canDropUpload || !isIncomingFileDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, [canDropUpload]);

  const onPanelDragEnter = useCallback((event) => {
    if (!canDropUpload || !isIncomingFileDrag(event)) return;
    event.preventDefault();
    setDragOverDepth((depth) => depth + 1);
  }, [canDropUpload]);

  const onPanelDragLeave = useCallback((event) => {
    if (!canDropUpload || !isIncomingFileDrag(event)) return;
    event.preventDefault();
    setDragOverDepth((depth) => Math.max(0, depth - 1));
  }, [canDropUpload]);

  const onPanelDrop = useCallback((event) => {
    if (!canDropUpload || !isIncomingFileDrag(event)) return;
    event.preventDefault();
    setDragOverDepth(0);
    // Client-side type filter (T-MCRO-035): a first line of defense only — the
    // server remains the authoritative validator (case B/E). A dropped file of
    // an unsupported type (e.g. .txt, .exe) is silently dropped from the batch
    // rather than enqueued for a chunked upload that the server would reject anyway.
    const files = Array.from(event.dataTransfer?.files || []).filter(isAcceptedUploadFile);
    if (files.length > 0) onFilesSelected(files);
  }, [canDropUpload, onFilesSelected]);

  const openFilePicker = useCallback(() => {
    if (canDropUpload && filePickerRef.current) filePickerRef.current.click();
  }, [canDropUpload]);

  const onFilePickerChange = useCallback((event) => {
    // Client-side type filter (T-MCRO-035) — see onPanelDrop above for why this
    // is UX-only, not authoritative.
    const files = Array.from(event.target.files || []).filter(isAcceptedUploadFile);
    if (files.length > 0) onFilesSelected(files);
    event.target.value = '';
  }, [onFilesSelected]);

  const expandedItem = expandedId ? viewItems.find((item) => mediaKey(item) === expandedId) : null;
  // MCRO001-F0040 / T-MCRO-040 Case B.
  const loadTrash = useCallback(async () => {
    if (typeof listTrash !== 'function') return;
    setTrash({ loading: true, media: [], retentionDays: trashRetentionDays });
    try {
      const response = await listTrash();
      const payload = response?.data?.data || response?.data || {};
      setTrash({
        loading: false,
        media: Array.isArray(payload.media) ? payload.media : [],
        // Prefer the SERVER's window over the capabilities prop: they are the same
        // value, but the trash response is the one computed alongside the
        // per-item daysRemaining, so it can never disagree with it.
        retentionDays: Number.isFinite(Number(payload.retentionDays)) ? Number(payload.retentionDays) : trashRetentionDays,
      });
    } catch (error) {
      setTrash({ loading: false, media: [], retentionDays: trashRetentionDays, error: libraryErrorMessage(error, 'Could not load Trash.') });
    }
  }, [listTrash, trashRetentionDays]);

  const openTrash = useCallback(() => { setShowTrash(true); loadTrash(); }, [loadTrash]);

  // MCRO001-F0039 / T-MCRO-039 Case G. The confirmation names BOTH consequences the
  // actor cannot see from here — that the video leaves every canvas it is on, for
  // everyone in the organisation, and that it is permanently deleted after the
  // configured window unless restored. The retention number is the configured one,
  // never a hardcoded 30.
  const deleteFromLibrary = useCallback(async (item) => {
    if (typeof onDeleteMedia !== 'function') return;
    const label = sensibleTitle(item);
    const ok = confirm(
      `Delete "${label}" from your video library?\n\n`
      + 'It will be removed from every canvas it is on, for everyone in this organisation, '
      + `and permanently deleted after ${trashRetentionDays} days unless you restore it from Trash.`,
    );
    if (!ok) return;
    const key = String(mediaKey(item));
    setDeletingIds((prev) => new Set(prev).add(key));
    setLibraryNotice('');
    try {
      await onDeleteMedia(item);
      setLibraryNotice(`Deleted ${label}. You can restore it from Trash for ${trashRetentionDays} days.`);
      if (showTrash) await loadTrash();
    } catch (error) {
      setLibraryNotice(libraryErrorMessage(error, `Could not delete ${label} from the library.`));
    } finally {
      setDeletingIds((prev) => { const next = new Set(prev); next.delete(key); return next; });
    }
  }, [onDeleteMedia, confirm, trashRetentionDays, showTrash, loadTrash]);

  // MCRO001-F0040 / T-MCRO-040 Case C.
  const restoreFromTrash = useCallback(async (entry) => {
    if (typeof onRestoreMedia !== 'function') return;
    const key = String(entry?.mediaId || '');
    setRestoringIds((prev) => new Set(prev).add(key));
    try {
      await onRestoreMedia(entry);
      setLibraryNotice(`Restored ${sensibleTitle(entry)} to your library.`);
      await loadTrash();
    } catch (error) {
      setTrash((prev) => ({ ...(prev || { media: [], retentionDays: trashRetentionDays }), loading: false, error: libraryErrorMessage(error, 'Could not restore that video.') }));
    } finally {
      setRestoringIds((prev) => { const next = new Set(prev); next.delete(key); return next; });
    }
  }, [onRestoreMedia, loadTrash, trashRetentionDays]);

  // MCRO001-F0053 / T-MCRO-053 Case A / D011 §5.3: "creates the folder as a
  // child of the currently open folder" — the caller's onCreateFolder already
  // knows the open folder (it owns `breadcrumb`/navigation), so this only ever
  // passes the name.
  const submitNewFolder = useCallback(async (event) => {
    event.preventDefault();
    const name = newFolderName.trim();
    if (!name || typeof onCreateFolder !== 'function' || creatingFolder) return;
    setCreatingFolder(true); setFolderNotice('');
    try {
      await onCreateFolder(name);
      setNewFolderName('');
      setShowNewFolderForm(false);
    } catch (error) {
      // T-MCRO-053 Cases D/L: depth cap and name validation are surfaced with
      // the server's own message, never a generic failure.
      setFolderNotice(libraryErrorMessage(error, 'Could not create that folder.'));
    } finally {
      setCreatingFolder(false);
    }
  }, [newFolderName, onCreateFolder, creatingFolder]);

  // T-MCRO-053 Case C: only `name` is sent — hierarchy is untouched.
  const submitRenameFolder = useCallback(async (folder, name) => {
    if (typeof onRenameFolder !== 'function') return;
    setFolderNotice('');
    try {
      await onRenameFolder(folder, name);
      setRenamingFolderId('');
    } catch (error) {
      setFolderNotice(libraryErrorMessage(error, `Could not rename "${folder.name}".`));
    }
  }, [onRenameFolder]);

  // MCRO001-D011 §4.4: archiving promotes the folder's direct children to its
  // own former parent and is recoverable — offer an immediate, in-session
  // "Undo" (restoreFolder) right in the same notice `deleteFromLibrary`'s
  // library-delete notice already uses, since there is no list-archived-
  // folders endpoint in the merged backend contract for a caller to browse
  // back to it later (flagged in the MR as a possible D011 gap).
  const archiveFolderAction = useCallback(async (folder) => {
    if (typeof onArchiveFolder !== 'function') return;
    setFolderNotice('');
    try {
      await onArchiveFolder(folder);
      setFolderNotice({ text: `Archived "${folder.name}".`, undoFolderId: folder.folderId, undoName: folder.name });
    } catch (error) {
      setFolderNotice(libraryErrorMessage(error, `Could not archive "${folder.name}".`));
    }
  }, [onArchiveFolder]);

  const undoArchiveFolder = useCallback(async (folderId, name) => {
    if (typeof onRestoreFolder !== 'function') return;
    try {
      await onRestoreFolder(folderId);
      setFolderNotice(`Restored "${name}".`);
    } catch (error) {
      setFolderNotice(libraryErrorMessage(error, `Could not restore "${name}".`));
    }
  }, [onRestoreFolder]);

  const closeMovePicker = useCallback(() => { setMovingFolder(null); setMovingMedia(null); setMovingSelection(null); }, []);
  const listFoldersForPickerSafe = useCallback(
    (parentFolderId) => (typeof listFoldersForPicker === 'function' ? listFoldersForPicker(parentFolderId) : Promise.resolve([])),
    [listFoldersForPicker],
  );

  // MCRO001-D011 §5.3a / T-MCRO-055: same authority as the existing per-item
  // "Move to folder…" action (PATCH /media/:mediaId, §5.1) — `canEdit` gates
  // it, not `canManageFolders` (folder CRUD is a narrower, separate bar).
  const canMoveMedia = canEdit && typeof onMoveMedia === 'function';

  // MCRO001-D011 §5.3a / T-MCRO-055: moves one or more media items to
  // `destinationFolderId`, one `PATCH /media/:mediaId` at a time — there is NO
  // batch-move endpoint in the merged backend contract (§8 Owned TODO), so this
  // is deliberately sequential and never claims a stronger guarantee than the
  // server actually gives. A mid-batch failure leaves earlier successes moved
  // and later ones exactly where they were; the notice always states the TRUE
  // succeeded/total counts, never a false "Moved N" when fewer than N actually
  // moved (T-MCRO-055 Case E).
  // `unresolvedCount` (GitLab WebUI #36 / T-MCRO-055 Case E hardening): the
  // number of ids the CALLER intended to move (drag payload or selection)
  // GitLab WebUI #36 design call (lane 8, jobs 142352/142672/142957/143115 —
  // eight independent real-deployed-page runs of the SAME assertion):
  // `mediaItems` here is now ALWAYS the full drag-payload/selection set, one
  // entry per id, EVERY id genuinely attempted — never pre-filtered against
  // `items` (this component's own display cache). That cache is read once on
  // load/after each move and can legitimately lag the real selection on a
  // real deployed page (refetches, pane switches, propagation) — and
  // second-guessing the payload against it was ITSELF the defect: an earlier
  // version of this function computed `total` from whatever subset of the
  // payload still had a row in `items`, so an id the cache had not caught up
  // to yet simply vanished from the count, and the notice read as a clean,
  // uncaveated success for whatever remained. There is no "unresolved"
  // class any more — only server outcomes. `onFolderDrop`/`onBreadcrumbDrop`
  // build a real (if minimal, `{ mediaId }`-only) target for every payload
  // id, so `mediaItems.length` IS the true total, and the server — the sole
  // authority on whether an id is genuinely movable — decides succeeded vs
  // failed via each individual PATCH's real outcome.
  const moveMediaItems = useCallback(async (mediaItems, destinationFolderId, destinationLabel) => {
    const total = mediaItems.length;
    if (total === 0 || typeof onMoveMedia !== 'function') return;
    const place = destinationLabel || 'Root';
    let succeeded = 0;
    let firstFailureMessage = '';
    // Sequential, not Promise.all — see the comment above for why concurrency
    // is deliberately not introduced here (matches the existing
    // BULK_MINT_MAX_CONCURRENCY-style caution this file already applies to
    // other per-tile request storms, per §5.3a's own wording).
    for (const item of mediaItems) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await onMoveMedia(item, destinationFolderId);
        succeeded += 1;
      } catch (error) {
        if (!firstFailureMessage) firstFailureMessage = libraryErrorMessage(error, 'it could not be moved.');
      }
    }
    if (succeeded === total) {
      setFolderNotice(`Moved ${total} video${total === 1 ? '' : 's'} to ${place}.`);
    } else if (succeeded === 0) {
      setFolderNotice(`Could not move ${total === 1 ? 'that video' : `those ${total} videos`} to ${place}: ${firstFailureMessage}`);
    } else {
      const failed = total - succeeded;
      setFolderNotice(`Moved ${succeeded} of ${total} videos to ${place} — ${failed} could not be moved: ${firstFailureMessage}`);
    }
  }, [onMoveMedia]);

  // MCRO001-D011 §5.3a: a compatible drag carries our own DRAG_MIME type in
  // `dataTransfer.types` — readable during dragenter/dragover even though the
  // payload itself (`getData`) is only readable on drop. Mirrors
  // `isIncomingFileDrag`'s own types-only check just above, so an incoming OS
  // file drag (Files, no DRAG_MIME) never satisfies this and never shows the
  // move-drop highlight (T-MCRO-055 Case F — the two drag consumers stay
  // mutually exclusive).
  const isMediaDragEvent = (event) => {
    const types = event?.dataTransfer?.types;
    if (!types) return false;
    return Array.from(types).includes(DRAG_MIME);
  };

  const readMediaDragPayload = (event) => {
    try {
      const raw = event.dataTransfer.getData(DRAG_MIME);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed?.mediaIds) ? parsed.mediaIds : [];
    } catch { return []; }
  };

  const onFolderDragEnter = useCallback((event, folder) => {
    if (!canMoveMedia || !isMediaDragEvent(event)) return;
    event.preventDefault();
    setDropTargetFolderId(folderKey(folder));
  }, [canMoveMedia]);

  const onFolderDragOver = useCallback((event, folder) => {
    if (!canMoveMedia || !isMediaDragEvent(event)) return;
    event.preventDefault();
    // MUST match the drag SOURCE's `effectAllowed` (onDragMediaStart above sets
    // 'copy' — shared, unchanged code, also used by the Canvas-attach drag).
    // Real-browser verified (Playwright, headless Chromium, T-MCRO-055): setting
    // 'move' here — which reads as the more semantically correct value for a
    // MOVE — silently breaks the drop in every real browser, because 'move' is
    // not a member of the 'copy' effectAllowed set, so the UA treats the
    // location as refusing the drop and fires `dragend` with NO `drop` event at
    // all. jsdom's DataTransfer stub does not enforce this and let a
    // `dropEffect: 'move'` version of this line pass every jsdom test green
    // while failing 100% of the time on screen — exactly the class of defect
    // this component's own `.mc-tile-icons` comment already warns about.
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }, [canMoveMedia]);

  const onFolderDragLeave = useCallback((event, folder) => {
    // A dragleave also fires when the pointer moves onto a CHILD element of
    // the tile (the glyph, the name, the menu button) — only actually clear
    // the highlight once the pointer has left the tile itself, matching the
    // standard `relatedTarget` containment check for this exact false-leave
    // problem.
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDropTargetFolderId((current) => (current === folderKey(folder) ? '' : current));
  }, []);

  const onFolderDrop = useCallback((event, folder) => {
    setDropTargetFolderId('');
    endDragGesture();
    if (!canMoveMedia) return;
    const mediaIds = readMediaDragPayload(event);
    if (mediaIds.length === 0) return;
    event.preventDefault();
    // GitLab WebUI #36 design call (lane 8): the drag payload is the
    // authoritative selection — attempt EVERY id in it, never pre-filter
    // against `items` (this component's own display cache, which can lag on
    // a real deployed page). Use the cached item where `items` happens to
    // have one (so the request carries whatever fields onMoveMedia's
    // implementation wants beyond mediaId), otherwise fall back to a minimal
    // `{ mediaId }` target — moveMedia/updateMedia only ever reads
    // `item.mediaId`, so a minimal target is a fully real, fully sufficient
    // move attempt, not a stub. The server is the sole authority on whether
    // that id is genuinely movable now; see moveMediaItems's own comment for
    // the incident this closes.
    // T-MCRO-158: ids come ONLY from the dragstart payload (already frozen at
    // gesture start); the id→item mapping reads the FROZEN gesture view, never
    // a re-read of whatever render state happens to be current at drop time —
    // a mid-gesture refetch must not change what this drop moves. (An id the
    // frozen view has no row for still gets its minimal `{ mediaId }` target —
    // the #36 ruling below is unchanged.)
    const itemsByKey = new Map(viewItems.map((item) => [mediaKey(item), item]));
    const targets = mediaIds.map((id) => itemsByKey.get(id) || { mediaId: id });
    moveMediaItems(targets, folder.folderId, folder.name);
  }, [canMoveMedia, viewItems, moveMediaItems, endDragGesture]);

  // MCRO001-D011 §5.3a: "the breadcrumb becomes an additional onDrop target
  // resolving to a folderId the same way its existing onNavigate already
  // resolves one per segment" — symmetric with the folder-tile handlers above.
  const onBreadcrumbDragEnter = useCallback((event, segment, index) => {
    if (!canMoveMedia || !isMediaDragEvent(event)) return;
    event.preventDefault();
    setDropTargetBreadcrumbIndex(index);
  }, [canMoveMedia]);

  const onBreadcrumbDragOver = useCallback((event, segment, index) => {
    if (!canMoveMedia || !isMediaDragEvent(event)) return;
    event.preventDefault();
    // Must match effectAllowed ('copy') — see onFolderDragOver's own comment
    // for the real-browser-only defect this avoids.
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }, [canMoveMedia]);

  const onBreadcrumbDragLeave = useCallback((event, segment, index) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDropTargetBreadcrumbIndex((current) => (current === index ? -1 : current));
  }, []);

  const onBreadcrumbDrop = useCallback((event, segment, index) => {
    setDropTargetBreadcrumbIndex(-1);
    endDragGesture();
    if (!canMoveMedia) return;
    const mediaIds = readMediaDragPayload(event);
    if (mediaIds.length === 0) return;
    event.preventDefault();
    // See onFolderDrop's own comment: attempt every payload id, never
    // pre-filter against the display cache, and map through the FROZEN
    // gesture view (T-MCRO-158), not the current render state.
    const itemsByKey = new Map(viewItems.map((item) => [mediaKey(item), item]));
    const targets = mediaIds.map((id) => itemsByKey.get(id) || { mediaId: id });
    moveMediaItems(targets, segment.folderId ?? null, segment.name);
  }, [canMoveMedia, viewItems, moveMediaItems, endDragGesture]);

  // MCRO001-D011 §5.3a: the non-drag "Move to…" selection-bar action resolves
  // through the SAME FolderDestinationPicker (movingSelection below) — the
  // destination's display name arrives as the picker's second `onChoose`
  // argument (see FolderDestinationPicker's own comment for why that argument
  // is new-but-backward-compatible).
  const moveSelectionTo = useCallback((destinationFolderId, destinationLabel) => (
    moveMediaItems(movingSelection || [], destinationFolderId, destinationLabel)
  ), [movingSelection, moveMediaItems]);

  const canDelete = canEdit && typeof onDeleteMedia === 'function';

  const selectedItems = viewItems.filter((item) => selectedIds.has(mediaKey(item)));
  const hasImages = viewItems.some(mediaIsImage);
  const showDropOverlay = canDropUpload && dragOverDepth > 0;

  // MCRO001-F0053 / T-MCRO-053: folder tiles are a structural/organisational
  // layer, not something the free-text media search reaches into (D011 has no
  // folder search endpoint) — while a search query is active the grid keeps
  // its EXISTING, unchanged, regression-tested search behaviour (media only);
  // folder tiles reappear once the query is cleared.
  const isRootFolder = viewBreadcrumb.length <= 1;
  const currentFolderLabel = viewBreadcrumb[viewBreadcrumb.length - 1]?.name || 'Root';
  const showFolderTiles = !query.trim() && viewFolders.length > 0;
  // MCRO001-D011 §4.3: mirrors the server's own depth cap, proactively —
  // see FOLDER_DEPTH_CAP's own comment for why this is a nicety, not the
  // authority.
  const atFolderDepthCap = (breadcrumb.length - 1) >= FOLDER_DEPTH_CAP;

  // MCRO001-F0053 / T-MCRO-053 integration with MCRO001-F0030 / T-MCRO-030
  // Case M (GitLab WebUI #20 "pending uploads"): a pending video-ingest
  // session carries NO folderId anywhere in the merged backend contract
  // (GET /video-ingests / choreographyVideoIngestService.listPending has no
  // folder field — the same D011 gap moveIntoOpenFolderIfNeeded's own comment
  // in MusicChoreography.js names, flagged in the MR rather than worked
  // around with an invented field). Rather than guess which folder a pending
  // row belongs to, it is shown ONLY at root, where showing it implies no
  // folder membership at all — never a fabricated one. Once the upload
  // completes, moveIntoOpenFolderIfNeeded has already moved the real media
  // row into whichever folder was open when the upload started, and it
  // appears there as an ordinary MediaTile; only the brief pending window is
  // root-only. This still satisfies T-MCRO-030 Case M's own guarantee
  // ("never render an empty library while any upload record exists") at
  // root, unchanged from before this Feature existed.
  const visiblePendingUploads = isRootFolder ? filteredPending : [];
  // #29: also dismissal-filtered (search-unfiltered only) — a fully-dismissed
  // set of terminal attempts must let the personal-library empty state return
  // (its copy is MCRO001-F0159's "Your library is empty — …" since D021 §1.3;
  // the pre-F0159 "No videos yet" wording is struck), not keep it permanently
  // blocked by an attempt the actor already cleared.
  const visiblePendingUploadsUnfiltered = isRootFolder ? undismissedPendingUploads : [];
  const canRenderListing = (itemsStatus === 'ready' && foldersStatus === 'ready')
    || (itemsStatus === 'ready' && (viewItems.length > 0 || visiblePendingUploads.length > 0))
    || (foldersStatus === 'ready' && viewFolders.length > 0);

  return (
    <div
      className="mc-library"
      aria-label="Media library"
      onDragOver={onPanelDragOver}
      onDragEnter={onPanelDragEnter}
      onDragLeave={onPanelDragLeave}
      onDrop={onPanelDrop}
    >
      {showDropOverlay && (
        <div className="mc-library-dropzone-overlay" role="status" aria-live="polite">
          <div className="mc-library-dropzone-message"><span aria-hidden="true">⇩</span> Drop to upload</div>
        </div>
      )}
      <div className="mc-library-main">
        {showTrash ? (
          <TrashView
            trash={trash}
            restoringIds={restoringIds}
            onRestore={restoreFromTrash}
            onClose={() => setShowTrash(false)}
          />
        ) : (
        <>
        <header className="mc-library-head">
          <div className="mc-library-title"><span className="mc-kicker">Video library</span><h2>Library</h2></div>
          <div className="mc-library-controls">
            <label className="mc-field mc-library-search"><span className="mc-visually-hidden">Search media</span>
              <input type="search" aria-label="Search media" placeholder="Search" value={query} onChange={(event) => setQuery(event.target.value)} />
            </label>
            {canDropUpload
              ? <button type="button" className="mc-primary mc-library-upload-picker" onClick={openFilePicker}>+ Upload</button>
              : <button type="button" className="mc-library-upload" disabled aria-disabled>Upload unavailable</button>}
            {canDropUpload && (
              <input
                ref={filePickerRef}
                type="file"
                multiple
                accept={ACCEPTED_UPLOAD_TYPES_ATTR}
                onChange={onFilePickerChange}
                className="mc-visually-hidden"
                aria-label="Choose video files to upload"
                tabIndex={-1}
              />
            )}
          </div>
        </header>

        {/* MCRO001-F0053 / T-MCRO-053 (MCRO001-D011 §5.3): "the grid gains a
            breadcrumb above the tile area". Only rendered once a caller has
            actually wired folder navigation (onOpenFolder) — a caller that
            never passes it keeps the pre-Feature grid exactly as it was.
            Owner ruling 2026-08-24 (folder placement): "in the video lib page
            what you have the path 'root' you should have [+Folder] beside" /
            "move and rename the '+ New folder' above" — the folder-create
            affordance moved OUT of the header controls (where it sat beside
            "+ Upload") and IN NEXT TO THE BREADCRUMB PATH ITSELF, one control
            moved, not duplicated.
            Owner ruling 2026-08-24 (Trash placement + icons): "move the
            [Trash] it position is really bad! I didn't even know it was
            there. move it beside the [+ Folder] [Trash] can you replace with
            icons so they don't take so much space" — Trash moved out of the
            header controls too, into this SAME row beside "+ Folder", and
            both became compact icon buttons (the discoverability complaint
            was about POSITION, not the icon glyphs themselves).
            Each of the three pieces here keeps its OWN, INDEPENDENT gate
            (onOpenFolder for the path; canManageFolders + onCreateFolder for
            "+ Folder"; listTrash for Trash) — a caller that wires only some
            of them still gets exactly those, alone, in this same row; this is
            a layout change, not a capability change. */}
        {(typeof onOpenFolder === 'function'
          || (canManageFolders && typeof onCreateFolder === 'function')
          || typeof listTrash === 'function') && (
          <div className="mc-folder-breadcrumb-row">
            {typeof onOpenFolder === 'function' && (
              <FolderBreadcrumb
                path={viewBreadcrumb}
                onNavigate={(index) => onOpenFolder(viewBreadcrumb[index]?.folderId ?? null, viewBreadcrumb[index]?.name)}
                dropTargetIndex={dropTargetBreadcrumbIndex}
                onSegmentDragEnter={onBreadcrumbDragEnter}
                onSegmentDragOver={onBreadcrumbDragOver}
                onSegmentDragLeave={onBreadcrumbDragLeave}
                onSegmentDrop={onBreadcrumbDrop}
              />
            )}
            <div className="mc-folder-breadcrumb-row-actions">
              {canManageFolders && typeof onCreateFolder === 'function' && (
                // T-MCRO-053 Case M (fix, 2026-08-19 — GitLab WebUI production
                // report "I have clicked '+ New folder' ... I have no idea
                // what it has done!!"): a pure aria-expanded flip is silent —
                // nothing about THIS element changes on screen, so a sighted
                // actor gets no feedback from the control they just pressed
                // (see .mc-library-new-folder[aria-expanded="true"],
                // unchanged in substance by becoming an icon button). The
                // accessible name and title stay the fuller, unambiguous
                // "Create folder" — only the VISIBLE glyph is compact, per
                // the owner's own "replace with icons" instruction.
                <button
                  type="button"
                  className="mc-quiet mc-library-icon-button mc-library-new-folder"
                  onClick={() => setShowNewFolderForm((value) => !value)}
                  aria-expanded={showNewFolderForm}
                  aria-label="Create folder"
                  title="Create folder"
                ><span aria-hidden="true">📁+</span></button>
              )}
              {/* MCRO001-F0040 / T-MCRO-040 Case B: the way into the recycle
                  bin. Only offered when the caller wired a listTrash — an
                  actor without video-management authority gets a 403 from
                  that endpoint, so the control is not shown rather than
                  shown-and-broken. Plain button, not a toggle: it REPLACES the
                  grid with TrashView, which has its own "Back to library"
                  return control, so no aria-pressed/expanded state belongs
                  here. */}
              {typeof listTrash === 'function' && (
                <button
                  type="button"
                  className="mc-quiet mc-library-icon-button mc-library-trash-toggle"
                  onClick={openTrash}
                  aria-label="Open Trash"
                  title="Open Trash"
                ><span aria-hidden="true">🗑</span></button>
              )}
            </div>
          </div>
        )}

        {showNewFolderForm && (
          <form className="mc-new-folder-form" onSubmit={submitNewFolder} aria-label={`New folder in ${currentFolderLabel}`}>
            {/* T-MCRO-053 Case M (fix, 2026-08-19): mc-visually-hidden belongs
                on the LABEL TEXT only (the <span>), same as the search field
                two dozen lines above — it must never sit on the wrapping
                <label>, which clips everything inside it, INCLUDING the real
                <input> the actor is meant to type into. That was the actual
                production defect: clicking "+ New folder" opened a form whose
                only visible controls were "Create" (disabled) and "Cancel" —
                the name field itself was clipped to 0x0px, indistinguishable
                from nothing having happened. */}
            <label className="mc-field"><span className="mc-visually-hidden">Folder name</span>
              <input
                ref={newFolderInputRef}
                value={newFolderName}
                onChange={(event) => setNewFolderName(event.target.value)}
                placeholder="Folder name"
                aria-label="Folder name"
                maxLength={255}
                onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); setShowNewFolderForm(false); setNewFolderName(''); } }}
              />
            </label>
            <button type="submit" className="mc-primary" disabled={!newFolderName.trim() || creatingFolder || atFolderDepthCap}>
              {creatingFolder ? 'Creating…' : 'Create'}
            </button>
            <button type="button" className="mc-quiet" onClick={() => { setShowNewFolderForm(false); setNewFolderName(''); }}>Cancel</button>
            {/* T-MCRO-053 Case D: proactive, client-side depth-cap notice — the
                server's own CHOREOGRAPHY_FOLDER_DEPTH_EXCEEDED remains the
                actual authority (see submitNewFolder's error handling above). */}
            {atFolderDepthCap && <span className="mc-muted mc-folder-depth-cap-note" role="status">"{currentFolderLabel}" is already at the deepest allowed level (5). Choose a shallower folder to add a new one.</span>}
          </form>
        )}

        {folderNotice && (
          <div className="mc-alert" role="status">
            {typeof folderNotice === 'string' ? folderNotice : (
              <>
                {folderNotice.text}
                {folderNotice.undoFolderId && (
                  <button type="button" className="mc-text-button" onClick={() => undoArchiveFolder(folderNotice.undoFolderId, folderNotice.undoName)}>Undo</button>
                )}
              </>
            )}
          </div>
        )}

        {itemsStatus === 'loading' && <p className="mc-empty" role="status">Loading media for this location…</p>}
        {itemsStatus === 'error' && <p className="mc-alert" role="alert">Media for this location could not be loaded: {itemsError}</p>}
        {foldersStatus === 'loading' && <p className="mc-empty" role="status">Loading folders for this location…</p>}
        {foldersStatus === 'error' && foldersError && <p className="mc-alert" role="alert">Folders for this location could not be loaded: {foldersError}</p>}

        {selectedItems.length > 0 && (
          <div className="mc-selection-bar" role="region" aria-label="Selection actions">
            <span className="mc-selection-count">{selectedItems.length} selected</span>
            <span
              className="mc-selection-drag"
              draggable
              onPointerDown={(event) => onTileGesturePointerDown(selectedItems[0], event)}
              onDragEnd={onTileGestureDragEnd}
              onDragStart={(event) => onDragMediaStart(event, selectedItems[0])}
              aria-label={`Drag ${selectedItems.length} selected onto the canvas`}
              title="Drag onto the canvas"
            >⠿ Drag to canvas</span>
            {onAddToCanvas && <button type="button" className="mc-quiet" onClick={() => onAddToCanvas(selectedItems)}>Add to canvas</button>}
            {onUseInRoutine && <button type="button" className="mc-quiet" onClick={() => onUseInRoutine(selectedItems[0], selectedItems)}>Timing editor</button>}
            {/* MCRO001-D011 §5.3a / T-MCRO-055: the non-drag accessibility path —
                "select (checkbox clicks) -> Move to… (one click) -> choose a
                folder (one click)" satisfies the owner's own one-to-two-clicks
                rule for an actor who never drags. Reuses the SAME
                FolderDestinationPicker the per-item menu action already uses
                (movingSelection below), extended to the whole selection. */}
            {canMoveMedia && <button type="button" className="mc-quiet" onClick={() => setMovingSelection(selectedItems)}>Move to…</button>}
            <button type="button" className="mc-quiet" onClick={clearSelection}>Clear</button>
          </div>
        )}

        {/* MCRO001-F0053 / T-MCRO-053: folder tiles render BEFORE media tiles
            in the same grid (MCRO001-D011 §5.3). `nothingMatchesFilter`
            decides whether the "no search results" branch renders instead of
            the grid, matching the pre-Feature behaviour byte-for-byte when a
            search query is active (folder tiles are excluded from search —
            see showFolderTiles above). The truly-empty invitation branch has
            distinct copy for an empty ROOT vs an empty FOLDER (a folder being
            empty is normal, not a dead end) — see isRootFolder below.

            MCRO001-F0030 / T-MCRO-030 Case M (GitLab WebUI #20 requirement 3,
            unchanged by this Feature): "never render an empty library while
            any upload record exists" — visiblePendingUploads/
            visiblePendingUploadsUnfiltered fold pending-ingest counts into
            both checks the SAME way the pre-folders code did, root-scoped
            (see their own comment for why). */}
        {canRenderListing && ((!showFolderTiles && filtered.length === 0 && visiblePendingUploads.length === 0)
          ? (viewItems.length === 0 && viewFolders.length === 0 && visiblePendingUploadsUnfiltered.length === 0
            ? (isRootFolder
              ? (
                // MCRO001-F0159 (MCRO001-D021 §1.3): the empty PERSONAL
                // library is a first-class state carrying this exact copy —
                // not an error, and never a fallback to an org-wide list.
                // The pre-F0159 copy ("No videos yet") is struck with the
                // org-wide library it described. The Upload affordance line
                // below stays: the D021 copy names the two ways forward
                // (upload, or open a canvas shared with you) and this wires
                // the first of them.
                <div className="mc-library-empty">
                  <div className="mc-library-empty-glyph" aria-hidden="true">▶</div>
                  <h3>Your library is empty — upload a video or open a canvas shared with you</h3>
                  <p>Drop files here or <button type="button" className="mc-text-button" onClick={openFilePicker} disabled={!canDropUpload}>Upload</button> to start your library.</p>
                </div>
              )
              : (
                <div className="mc-library-empty mc-folder-empty">
                  <div className="mc-library-empty-glyph" aria-hidden="true">📁</div>
                  <h3>"{currentFolderLabel}" is empty</h3>
                  <p>
                    Drop files here{canDropUpload ? <> or <button type="button" className="mc-text-button" onClick={openFilePicker}>Upload</button></> : ''}
                    {canManageFolders && <> — or <button type="button" className="mc-text-button" onClick={() => setShowNewFolderForm(true)} disabled={atFolderDepthCap}>create a subfolder</button></>}
                    {' '}to get started.
                  </p>
                </div>
              ))
            : <p className="mc-empty">No media matches that search.</p>)
          : (
            <div className="mc-grid" role="list">
              {showFolderTiles && viewFolders.map((folder) => (
                <div role="listitem" key={folderKey(folder)}>
                  <FolderTile
                    folder={folder}
                    canManage={canManageFolders}
                    onOpen={(target) => onOpenFolder(target.folderId, target.name)}
                    onRename={() => setRenamingFolderId(folder.folderId)}
                    onMove={() => setMovingFolder(folder)}
                    onArchive={() => archiveFolderAction(folder)}
                    renaming={renamingFolderId === folder.folderId}
                    onSubmitRename={(name) => submitRenameFolder(folder, name)}
                    onCancelRename={() => setRenamingFolderId('')}
                    isDropTarget={dropTargetFolderId === folderKey(folder)}
                    onDragEnterMedia={(event) => onFolderDragEnter(event, folder)}
                    onDragOverMedia={(event) => onFolderDragOver(event, folder)}
                    onDragLeaveMedia={(event) => onFolderDragLeave(event, folder)}
                    onDropMedia={(event) => onFolderDrop(event, folder)}
                  />
                </div>
              ))}
              {/* Pending/wedged/failed uploads lead the media tiles — they are,
                  by definition, the most recently touched items in the library
                  and the ones most likely to need the user's attention.
                  MCRO001-F0053 / T-MCRO-053: shown ONLY at root
                  (visiblePendingUploads) — see that constant's own comment for
                  why a pending row is never shown inside a specific folder. */}
              {visiblePendingUploads.map((row) => (
                <div role="listitem" key={`pending-${row?.uploadId || row?.title}`}>
                  <PendingUploadTile row={row} onRetryUpload={canDropUpload ? openFilePicker : undefined} onDismissUpload={dismissUpload} />
                </div>
              ))}
              {filtered.map((item) => (
                <div role="listitem" key={mediaKey(item)}>
                  <MediaTile
                    item={item}
                    orgId={orgId}
                    mintStreamUrl={mintStreamUrl}
                    selected={selectedIds.has(mediaKey(item))}
                    onToggleSelect={toggleSelect}
                    onExpand={openExpanded}
                    onDragMediaStart={onDragMediaStart}
                    onGesturePointerDown={onTileGesturePointerDown}
                    onGestureDragEnd={onTileGestureDragEnd}
                    quickPlaying={quickPlayId === mediaKey(item)}
                    onQuickPlay={setQuickPlayId}
                    onStopQuickPlay={() => setQuickPlayId('')}
                    onDelete={canDelete ? deleteFromLibrary : undefined}
                    deleting={deletingIds.has(String(mediaKey(item)))}
                    trackColour={trackColourForMedia(trackColours, mediaKey(item))}
                    onMoveToFolder={canEdit && typeof onMoveMedia === 'function' ? (target) => setMovingMedia(target) : undefined}
                  />
                </div>
              ))}
            </div>
          ))}

        {!hasImages && (
          <p className="mc-library-note mc-muted">Photos appear here alongside videos once image media exists in this routine.</p>
        )}

        {/* A failed (or successful) library delete/restore must never look like
            nothing happened. Shares the page's .mc-alert treatment. */}
        {libraryNotice && <div className="mc-alert" role="status">{libraryNotice}</div>}

        {expandedItem && (
          <ExpandedMedia
            key={expandedId}
            item={expandedItem}
            orgId={orgId}
            mintStreamUrl={mintStreamUrl}
            canEdit={canEdit}
            onSaveMetadata={onSaveMetadata}
            onClose={() => setExpandedId('')}
            onUseInRoutine={onUseInRoutine ? (item) => onUseInRoutine(item, [item]) : undefined}
            onAddToCanvas={onAddToCanvas}
            onOpenShare={typeof getMediaSharingAccess === 'function' ? openShare : undefined}
            restoreFocusRef={expandTriggerRef}
            onMoveToFolder={canEdit && typeof onMoveMedia === 'function' ? (target) => setMovingMedia(target) : undefined}
          />
        )}
        </>
        )}
      </div>

      {/* WebUI issue #30 / M001-D023 §7 §12.1: full Share management for
          choreo_media, the same conformant panel the Sheet uses. */}
      <ResourceSharePanel
        open={Boolean(shareItem && sharingApi)}
        resourceLabel={shareItem ? sensibleTitle(shareItem) : ''}
        resourceKey={shareId}
        api={sharingApi}
        onClose={() => setShareId('')}
      />

      {/* MCRO001-F0053 / T-MCRO-053 (MCRO001-D011 §5.3): exactly one of
          movingFolder/movingMedia is set at a time — see their setters above. */}
      {movingFolder && (
        <FolderDestinationPicker
          title={`Move folder "${movingFolder.name}"`}
          listFolders={listFoldersForPickerSafe}
          excludeFolderId={folderKey(movingFolder)}
          onChoose={(destinationFolderId) => onMoveFolder(movingFolder, destinationFolderId)}
          onClose={closeMovePicker}
        />
      )}
      {movingMedia && (
        <FolderDestinationPicker
          title={`Move "${sensibleTitle(movingMedia)}"`}
          listFolders={listFoldersForPickerSafe}
          onChoose={(destinationFolderId) => onMoveMedia(movingMedia, destinationFolderId)}
          onClose={closeMovePicker}
        />
      )}
      {/* MCRO001-D011 §5.3a / T-MCRO-055: the selection-bar "Move to…" path —
          the SAME picker component, but resolving through moveSelectionTo
          (sequential per-item PATCH, honest partial-failure reporting via
          folderNotice) rather than a single onMoveMedia call that can throw
          and reopen the dialog. See moveSelectionTo's own comment. */}
      {movingSelection && (
        <FolderDestinationPicker
          title={movingSelection.length === 1 ? `Move "${sensibleTitle(movingSelection[0])}"` : `Move ${movingSelection.length} selected`}
          listFolders={listFoldersForPickerSafe}
          onChoose={(destinationFolderId, destinationLabel) => moveSelectionTo(destinationFolderId, destinationLabel)}
          onClose={closeMovePicker}
        />
      )}
    </div>
  );
}
